'use client';

/**
 * EntityGraph — the per-chain relationship map (Task 3).
 *
 * A force-directed graph sitting ABOVE the <Directory> on `/<chain>/entities`
 * (graph on top, directory below — NOT a toggle; the toggle was killed in chat1.md).
 *
 * Faithful port of the prototype `useForce` physics
 * (docs/design/descout-dashboard-prototype/app/graph.jsx): repulsion 2600/d²,
 * collision (minD = r+r+34, ×0.12), spring target 118 @0.045, gravity 0.012 to
 * center, velocity damping 0.82, a ~260-step synchronous settle for a tidy first
 * paint, and a short RAF cooldown. Pointer-drag a node to reposition it.
 *
 * ADAPTER (thin, by design): entities = nodes. TWO edge sources:
 *   1. CONFIRMED typed edges (`entity_relationships`, server-fetched, passed as
 *      the `typedEdges` prop) — first-class: a notch stronger gold stroke, a
 *      perspective LABEL ("founded by" / "mascot of") at the edge midpoint when
 *      its node is focused (never permanently — that's noise at 100+ entities),
 *      and the narrative `description` in the inspector rail. Typed edges may
 *      reach an off-chain GUEST node (below) — but always ≥1 member endpoint.
 *   2. `entity.related` (GENERIC associations) — the faint fallback edge,
 *      drawn ONLY for pairs with no typed edge (typed wins; density is kept),
 *      and ONLY in-chain (BOTH endpoints members — NEVER relaxed: associatedTags
 *      point anywhere, incl. chain proxies → density explosion).
 * Typed metadata lives in a pair-keyed map BESIDE the force sim — the hot loop
 * still only sees cheap {source, target} links.
 *
 * GUESTS (the FUGZ fix, Lam-approved 2026-06-10): off-chain entities that a
 * confirmed TYPED edge pulls 1 hop into the graph arrive via the `guests` prop
 * (server-projected `ChainEntity` rows with `offChain` = their home chain id).
 * They render at FULL opacity like members (de-ghosted 2026-06-24 — an off-chain
 * entity is a real place, not a faded visitor); the "lives elsewhere" cue is the
 * dashed `3 3` type ring + a home-chain micro-badge bottom-right (◆n style, e.g.
 * "ETH") + a "Cross-chain" legend chip, and a "Lives on <Chain>" banner atop the
 * inspector. Guests never seed expansion: no generic edges, no asset satellites,
 * and they're EXCLUDED from the header's member coverage count.
 *
 * ASSETS (the JUNCTION RULE — docs/superpowers/specs/2026-06-10-graph-asset-
 * junction-rule-design.md): an asset earns canvas presence only when it connects
 * ≥2 member entities (a junction — real topology, e.g. Giglings between Gigaverse
 * and Gigling Racing). Junction assets render as small dashed DIAMOND nodes on
 * dotted "asset of" edges. Single-parent leaves + identity-only assets stay OFF
 * the canvas by default — they live in the inspector's ASSETS portfolio block,
 * and their parent entity wears a tiny `◆n` count badge instead of a leaf fan.
 * The legend's "Assets" chip cycles three states: shared (default) → all → off.
 * Hidden assets are EXCLUDED from the force sim entirely (no invisible forces
 * distorting the default layout); on toggle the kept nodes hold their settled
 * positions and only the entering/leaving satellites re-settle around them.
 * Clicking a satellite opens the compact ASSET inspector card (parents +
 * "View market →" deep-link) — unchanged.
 *
 * SELECTION is CONTROLLED (the unified-inspector model): the page-level
 * <EntitiesExplorer> owns `selectedId` and feeds BOTH this graph AND the
 * <Directory> below — a directory-card click selects into the SAME inspector
 * (no second preview surface; the darkening EntityPreviewDrawer is retired).
 * Node click → the inspector: a NON-OBSCURING side rail on desktop (never darkens
 * the canvas), a fixed BOTTOM SHEET at <768px (portaled to <body>, dismissable
 * via backdrop/×/Esc, NO page scroll lock). It carries identity + RECON + Relationships +
 * recent coverage + a prominent "View full profile →" jump to `/tags/[slug]`.
 *
 * MOBILE gestures: one-finger background drag = pan, one-finger node drag = move
 * node, TWO fingers = pinch-to-zoom (alongside wheel + the +/− buttons); the
 * layout seeds a smaller radius at narrow widths and fit-to-bounds frames the
 * whole graph on open. The graph is a client island; the crawlable entity
 * surface is the SSR <Directory> below + `/tags/[slug]`.
 *
 * TYPE STRUCTURE (R5): minority entity types (person / organization / protocol)
 * feel a gentle gravity well toward their own ring anchor so the type groups
 * parse spatially (springs still dominate — see TYPE_WELL_ANGLE). The header
 * carries the coverage strip: entities mapped · connections · % of directory.
 *
 * MOTION (R7): restrained perpetual drift (whisper-alpha sim tick, ~30fps),
 * a spring re-settle pulse + one-shot ping ring on select, fading hover halos,
 * and entrance motion on the inspector (rail fade-up / sheet slide-up).
 * `prefers-reduced-motion` gates ALL of it: the JS-driven motion via
 * usePrefersReducedMotion(), every CSS animation/transition (including inline
 * ones like the sheet's spring-back) via the `eg-motion-scope` media rule in
 * <GraphMotionStyles> — applied to the graph <section> AND the portaled sheet.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * ORBITAL LAYOUT (EVALUATION PROTOTYPE — `layoutMode`, default 'orbit')
 * ───────────────────────────────────────────────────────────────────────────
 * The organic force layout reads too dense to some eyes, so this file carries a
 * SECOND, fully deterministic layout behind a legend toggle ("Layout: Orbit ↔
 * Force") so the product owner can flip live and decide. It is a PROTOTYPE for
 * evaluation, NOT a replacement: the force layout (the whole `useForce` sim
 * above) is untouched and remains one flip away. Every node stays visible in
 * BOTH modes — an earlier "fold/hide" experiment was rejected.
 *
 * The orbital positions are computed ONCE in a pure useMemo (`useOrbitalLayout`)
 * from the SAME drawn graph the renderer uses (nodes + links), so they are
 * identical every load. The algorithm lays the graph out as clean PIE-SLICE
 * communities around the most-connected hubs:
 *
 *   1. HUBS  — entity nodes with degree ≥ 5, sorted degree DESC then slug ASC,
 *      capped at 8. With <2 hubs there is nothing to orbit around, so orbital
 *      mode is UNAVAILABLE (the chip hides, force mode is used regardless).
 *   2. SECTORS — every non-hub node is assigned a HOME hub = the nearest hub by
 *      a multi-source BFS over the drawn links (ties → the earlier hub in the
 *      sorted order). Each hub owns a contiguous angular WEDGE whose width is
 *      proportional to (1 + its member count), normalized to 2π, laid out in
 *      hub order from -π/2 (12 o'clock). A hub sits at its wedge's CENTER angle.
 *   3. RINGS  — radius by BFS hop distance from the home hub: hub (hop 0) =
 *      0.15·min(W,H); hop 1 = 0.30; hop 2 = 0.42; hop ≥3 = 0.52 — all measured
 *      from the canvas center (W/2, H/2). (Tightened from 0.18/0.40/0.56/0.70
 *      after PO feedback "too spread apart"; the tighter footprint also raises
 *      the fit zoom, and resting edge strokes carry extra weight in orbit mode
 *      — see the edge stroke ternary — since geometry, not proximity, encodes
 *      the relationships here.)
 *   4. ANGLE  — within a wedge, each (hub, ring) cohort is sorted by its members'
 *      BFS-PREDECESSOR angle (then slug) and spread EVENLY across the wedge span
 *      (inset by a small margin) so children sit angularly near their parent and
 *      communities read as clean slices. If a cohort is too crowded (arc length
 *      per node < node diameter + 6px) it splits into two interleaved sub-rings
 *      at r ± 14px (alternate members) to relieve the crowding.
 *   5. FLOATERS — nodes unreachable from any hub (incl. ALL-mode zero-edge
 *      members) sit on an outer rim at 0.62·min(W,H), GROUPED BY CONNECTED
 *      COMPONENT (over the same entity/guest adjacency; asset edges excluded) so
 *      island partners sit side by side and their edges become short arcs, not
 *      full-circle chords. Components are ordered (size DESC, then smallest-member-
 *      slug ASC) and placed consecutively; within a component, members follow BFS
 *      visit order from the smallest-slug member (slug fallback). One empty slot
 *      separates components (folded into the even spacing) so islands read as
 *      distinct clumps. If the rim is overcrowded (arc per slot < node diameter +
 *      6px) it splits into two interleaved rims at 0.58/0.66·min(W,H) — WHOLE
 *      components alternate rims, never split across them.
 *   6. ASSETS  — satellite (kind==='asset') nodes do NOT consume wedge/ring
 *      slots; each is parked beside its first member parent at parent + 40px,
 *      angles spread evenly per parent.
 *   7. GUESTS  — flow through the normal assignment (they are nodes with typed
 *      edges), so off-chain guests land in the wedge of whichever hub pulls them.
 *
 * MODE MECHANICS: in orbit mode `useForce` writes the orbital positions straight
 * into the SAME `pos` map the renderer + drag + fit already read, and runs NO
 * repulsion/spring/gravity/settle/drift — positions are exact. Node DRAG still
 * works (it writes the pos map directly; with no sim there is no spring-back).
 * Pan/zoom/fit are unchanged; the mode is folded into the fit + sim keys so a
 * flip re-fires fit-to-bounds and re-snaps positions. Everything else (selection,
 * inspector, hover, lenses/type/guests/sentiment dims, path mode + BFS, edge
 * mode, deep links) keys off node ids + the pos map and so composes unchanged.
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import { createPortal } from 'react-dom';
import Link from 'next/link';
import { getChain } from '@/lib/chains';
import { Avatar, Eyebrow, Mono, initials, useIsMobile } from './atoms';
import { ENTITY_TYPES, type EntityTypeKey } from './format';
import EntityProfileContent from './EntityProfileContent';
import type {
  ChainAssetNode,
  ChainEntity,
  ChainTypedEdge,
} from '@/lib/hub/chain-entities-data';
// The PURE display rules (ontology family → stroke/arrow, confidence → alpha,
// typed degree → radius, type → ring color, ego-core selection). Unit-tested
// without a DOM in src/lib/hub/__tests__/graph-style.test.ts.
import {
  typedEdgeStyle,
  edgeGeometry,
  typeRingColor,
  nodeRadius,
  buildTypedTopology,
  selectEgoCore,
  egoSeedIds,
  egoSeedCount,
  rimLabelCap,
  labelBudget,
  restingLabelIds,
  TYPED_EDGE_COLOR,
  INFERRED_EDGE_COLOR,
  INFERRED_EDGE_DASH,
  INFERRED_EDGE_WIDTH,
  INFERRED_EDGE_ALPHA,
  TYPE_RING,
  type EgoNode,
} from '@/lib/hub/graph-style';

/* ── graph model ─────────────────────────────────────────────────────────── */

interface GNode {
  id: string;
  kind: 'entity' | 'asset';
  /** Present when kind==='entity'. */
  ent?: ChainEntity;
  /** Present when kind==='asset'. */
  asset?: ChainAssetNode;
  r: number;
  /** DRAWN degree (typed + inferred + asset links) — the layout/hub signal. */
  deg: number;
  /** CONFIRMED typed-edge degree — the only thing that sizes a node (audit §3). */
  typedDeg: number;
}
interface GLink {
  source: string;
  target: string;
  /** True when this pair carries ≥1 confirmed typed edge (styling flag only —
      the heavy typed metadata lives in `typedByPair`, outside the hot loop). */
  typed: boolean;
  /** True for an entity↔asset satellite edge (dotted, short spring, "asset of"). */
  asset?: boolean;
}

/** Undirected pair key — shared by the link builder, edge labels and the rail. */
const pairKey = (a: string, b: string) => (a < b ? `${a}|${b}` : `${b}|${a}`);

/** localStorage key for the per-viewer "show inferred links" preference. */
const INFERRED_PREF_KEY = 'descout:graph:showInferred';


/** Legend "Layout" chip — two-state cycle between the deterministic ORBITAL
    prototype (default) and the organic FORCE sim. See the header doc-comment's
    ORBITAL LAYOUT section. */
type LayoutMode = 'orbit' | 'force';
const LAYOUT_MODE_LABEL: Record<LayoutMode, string> = {
  orbit: 'Layout: Orbit',
  force: 'Layout: Force',
};
const NEXT_LAYOUT_MODE: Record<LayoutMode, LayoutMode> = { orbit: 'force', force: 'orbit' };

/** Legend "Assets" chip — 3-state cycle (junctions are the default canvas truth). */
type AssetMode = 'shared' | 'all' | 'none';
const ASSET_MODE_LABEL: Record<AssetMode, string> = {
  shared: 'Assets: shared',
  all: 'Assets: all',
  none: 'Assets: off',
};
const NEXT_ASSET_MODE: Record<AssetMode, AssetMode> = { shared: 'all', all: 'none', none: 'shared' };

// The relationship-type lenses (Founders / Structure / Linked) were fully removed
// 2026-06-24 (buggy — Lam). The "Lens" dropdown now carries only the Sentiment
// overlay + Path finder; the per-edge-type dim machinery is gone with them.

/**
 * True when `a` is `e`'s IDENTITY TOKEN — the asset already rendered as the
 * inspector header's `$SYM` chip, so it never duplicates as a portfolio row or
 * a badge count. Identity NFT COLLECTIONS have no header chip and DO stay rows
 * (Wolf Game's eponymous collection is one of its four portfolio rows).
 */
const isIdentityTokenOf = (e: ChainEntity, a: ChainAssetNode) =>
  a.assetType === 'token' &&
  e.linkedAsset?.assetType === 'token' &&
  e.linkedAsset.assetId.toLowerCase() === a.assetId.toLowerCase();

/** Perspective label of a typed edge as seen FROM `slug`. */
const labelFrom = (te: ChainTypedEdge, slug: string) =>
  te.fromSlug === slug ? te.forwardLabel : te.inverseLabel;

/** GUEST home-chain display label ('ethereum' → 'Ethereum'); null for the
    chain-agnostic 'other' sentinel (still a guest — just no named home). */
const guestHomeLabel = (home: string | undefined): string | null => {
  if (!home || home === 'other') return null;
  return getChain(home)?.label ?? home.charAt(0).toUpperCase() + home.slice(1);
};
/** GUEST node micro-badge text ('ethereum' → 'ETH') — ◆n-style, bottom-right. */
const guestBadge = (home: string | undefined): string | null => {
  const label = guestHomeLabel(home);
  return label ? label.slice(0, 3).toUpperCase() : null;
};

/** A typed edge resolved for the inspector rail (from the selected node's POV). */
interface RailRelationship {
  label: string;
  other: ChainEntity;
  description: string | null;
  confidence: ChainTypedEdge['confidence'];
}
/** A directed typed edge resolved for the inspector's EDGE mode (edge-as-first-class). */
interface RailEdge {
  from: ChainEntity;
  to: ChainEntity;
  /** Forward perspective label (from → to), e.g. 'founded', 'developer of'. */
  label: string;
  /** The relationship TYPE token (e.g. 'FOUNDED') for the A —TYPE→ B header. */
  type: string;
  description: string | null;
  confidence: ChainTypedEdge['confidence'];
  /** Corroboration count only (never the raw sources). */
  sourceCount: number;
  /** Source HOSTNAMES only (deduped, capped server-side) — the "who says so". */
  sourceHosts: string[];
  /** Role on the edge ('co-founder', 'CTO', 'publisher'), when recorded. */
  role: string | null;
  /** ISO date the relationship began, when recorded. */
  startDate: string | null;
  /** ISO create timestamp, or null. */
  createdAt: string | null;
}
/** One hop of a resolved path, for the inspector's PATH card (A —label→ B …). */
interface RailPathHop {
  /** The "from" node of this hop (an entity, or an asset junction). */
  from: { name: string; image?: string; slug: string; isAsset: boolean };
  to: { name: string; image?: string; slug: string; isAsset: boolean };
  /** Perspective label from→to ('founded', 'parent of', 'asset of'), or null. */
  label: string | null;
  /** Hop narrative (typed edges only), or null. */
  description: string | null;
  /** True when this hop is a typed (first-class) edge. */
  typed: boolean;
}

/** Pointer handlers spread on the bottom sheet's grab-handle/header drag region. */
interface SheetDragHandlers {
  onPointerDown: (e: ReactPointerEvent<HTMLDivElement>) => void;
  onPointerMove: (e: ReactPointerEvent<HTMLDivElement>) => void;
  onPointerUp: (e: ReactPointerEvent<HTMLDivElement>) => void;
  onPointerCancel: (e: ReactPointerEvent<HTMLDivElement>) => void;
}
interface P {
  x: number;
  y: number;
  vx: number;
  vy: number;
}

/* ── motion (R7) ─────────────────────────────────────────────────────────── */

/**
 * Live `prefers-reduced-motion` flag. Gates the JS-DRIVEN motion (perpetual
 * drift, the select re-settle pulse, smooth scrollIntoView); the CSS-driven
 * motion is gated in the stylesheet itself (see <GraphMotionStyles>), which
 * also covers the pre-hydration frame this hook can't (it starts `false` on
 * the server for hydration stability, same tradeoff as useIsMobile).
 */
function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = () => setReduced(mq.matches);
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return reduced;
}

/**
 * Graph motion keyframes + the reduced-motion KILL SWITCH. Everything motion-
 * scoped (the graph <section> AND the portaled phone sheet) carries the
 * `eg-motion-scope` class, so ONE media query gates ALL nonessential CSS
 * animation/transition in the graph — including inline `transition` styles
 * (the sheet's spring-back), which `!important` in a stylesheet overrides.
 * Direct-manipulation transforms (finger-follow drag) are NOT transitions and
 * are untouched, as they should be.
 */
function GraphMotionStyles() {
  return (
    <style>{`
@keyframes eg-select-ping { from { transform: scale(1); opacity: 0.55; } to { transform: scale(1.9); opacity: 0; } }
@keyframes eg-rail-in { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }
@keyframes eg-sheet-in { from { transform: translate3d(0, 100%, 0); } to { transform: translate3d(0, 0, 0); } }
@keyframes eg-fade-in { from { opacity: 0; } to { opacity: 1; } }
@keyframes eg-menu-in { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: translateY(0); } }
.eg-menu-pop { animation: eg-menu-in 120ms ease-out; transform-origin: bottom left; }
/* TOUCH TARGETS (review item 7). Every chip carries min-h-[36px] inline, which
   is the pointer-precise desktop density; on a COARSE pointer they grow to the
   44px WCAG 2.5.5 / platform-HIG target via padding only, so the desktop layout
   is untouched. Scoped to the graph section so nothing else on the page shifts. */
@media (pointer: coarse) {
  .eg-motion-scope button { min-height: 44px; }
}
@media (prefers-reduced-motion: reduce) {
  .eg-motion-scope, .eg-motion-scope * { animation: none !important; transition: none !important; }
}
`}</style>
  );
}

/* ── per-type gravity wells (R5) ─────────────────────────────────────────── */

/**
 * Ring anchors for the MINORITY entity types so Games/People/Protocols parse
 * spatially: person upper-left, organization upper-right, protocol bottom.
 * project/game (the bulk of every chain) keep plain center gravity — anchoring
 * the majority would just list the whole graph to one side. Well strength
 * stays well below the spring constant (0.045), so REAL links still dominate:
 * a founder typed-edged to her game sits between her cluster and the game, not
 * exiled to the person corner. Angles in radians; +y is DOWN in SVG.
 */
const TYPE_WELL_ANGLE: Record<string, number> = {
  person: -2.35, // upper-left
  organization: -0.78, // upper-right
  protocol: 1.95, // lower-middle
};

/* ── force layout (faithful port of the prototype's useForce) ────────────── */

function useForce(
  nodes: GNode[],
  links: GLink[],
  W: number,
  H: number,
  mounted: boolean,
  /** False under prefers-reduced-motion: no perpetual drift, no pulse, layout
      changes settle SYNCHRONOUSLY instead of animating (R7). */
  animate: boolean,
  /** Active layout: 'force' = the sim below (today's behavior); 'orbit' = the
      deterministic ORBITAL prototype — positions come from `orbitalPos`, no
      repulsion/spring/gravity/settle/drift runs at all. */
  layoutMode: LayoutMode,
  /** The deterministic orbital position map (node id → fixed {x,y}); read only
      when layoutMode==='orbit'. */
  orbitalPos: Record<string, P>,
) {
  const pos = useRef<Record<string, P>>({});
  const [, setTick] = useState(0);
  const raf = useRef<number | null>(null);
  const drag = useRef<string | null>(null);
  const inited = useRef('');
  // Select re-settle pulse (R7): a decaying alpha boost consumed by the drift
  // loop — pulse() momentarily re-energizes the sim so the neighbourhood
  // visibly re-seats around the chosen node, then breathes back down.
  const alphaBoost = useRef(0);

  // The mode is folded into the (re)seed key so flipping Orbit↔Force re-runs the
  // seed/settle branch below (force→orbit snaps to exact positions; orbit→force
  // re-seeds from the current — orbital — positions, which the sim then relaxes).
  const key = nodes.map((n) => n.id).join('|') + '@' + Math.round(W) + 'x' + H + '#' + layoutMode;

  function step(alpha: number) {
    const m = pos.current;
    const cx = W / 2;
    const cy = H / 2;
    const ids = nodes.map((n) => n.id);
    // repulsion + collision — ASSET satellites carry a weaker repulsion footprint
    // (they hug their parents instead of pushing the entity layout apart).
    for (let i = 0; i < ids.length; i++) {
      const a = m[ids[i]];
      const na = nodes[i];
      for (let j = i + 1; j < ids.length; j++) {
        const b = m[ids[j]];
        const nb = nodes[j];
        const dx = a.x - b.x;
        const dy = a.y - b.y;
        const d2 = dx * dx + dy * dy || 0.01;
        const d = Math.sqrt(d2);
        const isAssetPair = na.kind === 'asset' || nb.kind === 'asset';
        const minD = na.r + nb.r + (isAssetPair ? 18 : 64);
        let f = ((isAssetPair ? 800 : 5600) / d2) * alpha;
        if (d < minD) f += (minD - d) * 0.12 * alpha; // collision
        const fx = (dx / d) * f;
        const fy = (dy / d) * f;
        a.vx += fx;
        a.vy += fy;
        b.vx -= fx;
        b.vy -= fy;
      }
    }
    // springs — asset edges are shorter + stiffer (satellite orbit, not a peer link)
    links.forEach((l) => {
      const a = m[l.source];
      const b = m[l.target];
      if (!a || !b) return;
      const dx = b.x - a.x;
      const dy = b.y - a.y;
      const d = Math.sqrt(dx * dx + dy * dy) || 0.01;
      const f =
        (d - (l.asset ? 62 : l.typed ? 150 : 230)) *
        (l.asset ? 0.07 : l.typed ? 0.045 : 0.012) *
        alpha;
      const fx = (dx / d) * f;
      const fy = (dy / d) * f;
      a.vx += fx;
      a.vy += fy;
      b.vx -= fx;
      b.vy -= fy;
    });
    // gravity + integrate — entities of a MINORITY type split their gravity
    // between the canvas center and their type's ring anchor (gentle per-type
    // gravity wells, R5): the type groups drift apart enough to PARSE without
    // overruling the springs. Assets + the project/game bulk: center only.
    const wellR = Math.min(W, H) * 0.32;
    nodes.forEach((n) => {
      const p = m[n.id];
      const wellAngle = n.kind === 'entity' ? TYPE_WELL_ANGLE[n.ent!.type] : undefined;
      if (wellAngle !== undefined) {
        const wx = cx + Math.cos(wellAngle) * wellR;
        const wy = cy + Math.sin(wellAngle) * wellR;
        p.vx += ((wx - p.x) * 0.009 + (cx - p.x) * 0.004) * alpha;
        p.vy += ((wy - p.y) * 0.009 + (cy - p.y) * 0.004) * alpha;
      } else {
        p.vx += (cx - p.x) * 0.006 * alpha;
        p.vy += (cy - p.y) * 0.006 * alpha;
      }
      if (drag.current === n.id) return;
      p.x += p.vx *= 0.82;
      p.y += p.vy *= 0.82;
      p.x = Math.max(n.r + 8 - W * 0.35, Math.min(W * 1.35 - n.r - 8, p.x));
      p.y = Math.max(n.r + 8 - H * 0.35, Math.min(H * 1.35 - n.r - 8, p.y));
    });
  }

  // (re)seed positions + a synchronous settle whenever topology/size changes, so
  // the first paint is already tidy. Gated on `mounted` (client-only): the layout
  // uses transcendental math (cos/sqrt) that differs in the ~5th decimal between
  // Node and Chromium, so seeding on the server would trip a hydration mismatch.
  // The graph carries no SEO weight (the crawlable surface is the <Directory>
  // below + /tags/[slug]) — so SSR renders an empty canvas and we settle on mount.
  // ORBIT MODE: positions are EXACT and deterministic — copy the orbital map
  // into `pos` and run NO settle. (Drag still mutates pos directly; with no sim
  // there is no spring-back.) Falls through to the force seed below only when an
  // orbital position is somehow missing (defensive — keeps every node placed).
  if (mounted && inited.current !== key && layoutMode === 'orbit' && W > 0 && nodes.length > 0) {
    const m: Record<string, P> = {};
    for (const n of nodes) {
      const o = orbitalPos[n.id];
      m[n.id] = o
        ? { x: o.x, y: o.y, vx: 0, vy: 0 }
        : // missing orbital pos (shouldn't happen) → reuse prior or canvas center
          pos.current[n.id]
          ? { ...pos.current[n.id], vx: 0, vy: 0 }
          : { x: W / 2, y: H / 2, vx: 0, vy: 0 };
    }
    pos.current = m;
    inited.current = key;
  } else if (mounted && inited.current !== key && W > 0 && nodes.length > 0) {
    const prev = pos.current;
    const m: Record<string, P> = {};
    const cx = W / 2;
    const cy = H / 2;
    // Width-aware seed radius: narrow canvases (phones) seed a tighter ring so the
    // settle doesn't slam nodes into the side clamps (mobile ergonomics, R4).
    const seedBase = Math.max(80, Math.min(150, W * 0.22));
    const seedSpread = Math.max(28, Math.min(60, W * 0.08));
    // POSITION REUSE (asset-mode toggles): nodes that survive a topology change
    // keep their settled spot (velocity zeroed); only NEW nodes get seeded —
    // next to an already-placed link neighbour when they have one (satellites
    // pop in beside their parent), else on the classic ring.
    let reused = 0;
    const fresh: GNode[] = [];
    for (const n of nodes) {
      const p = prev[n.id];
      if (p) {
        m[n.id] = { x: p.x, y: p.y, vx: 0, vy: 0 };
        reused++;
      } else {
        fresh.push(n);
      }
    }
    fresh.forEach((n, i) => {
      const a = (i / Math.max(1, fresh.length)) * Math.PI * 2;
      const nb = links.find(
        (l) => (l.source === n.id && m[l.target]) || (l.target === n.id && m[l.source]),
      );
      if (nb) {
        const q = m[nb.source === n.id ? nb.target : nb.source];
        m[n.id] = { x: q.x + Math.cos(a) * 30, y: q.y + Math.sin(a) * 30, vx: 0, vy: 0 };
      } else {
        const rad = seedBase + (i % 3) * seedSpread;
        m[n.id] = { x: cx + Math.cos(a) * rad, y: cy + Math.sin(a) * rad, vx: 0, vy: 0 };
      }
    });
    pos.current = m;
    inited.current = key;
    // Incremental change (most nodes kept their spot — an assets toggle): a short,
    // gentle re-settle that doesn't reshuffle the kept layout. Fresh graph: the
    // full prototype settle for a tidy first paint.
    const incremental = reused > 0 && reused >= nodes.length * 0.5;
    const steps = incremental ? 80 : 260;
    const a0 = incremental ? 0.3 : 0.9;
    for (let k = 0; k < steps; k++) step(a0 * Math.pow(0.96, k));
  }

  // gentle live cooldown on mount / resize / topology change (only once seeded).
  // Under prefers-reduced-motion the same refinement happens SYNCHRONOUSLY in
  // one paint — the layout result is identical, only the animation is skipped.
  useEffect(() => {
    if (!mounted || nodes.length === 0 || !pos.current[nodes[0].id]) return;
    if (layoutMode === 'orbit') return; // exact positions — no live refinement
    if (!animate) {
      for (let k = 0; k < 140; k++) step(0.5 * Math.pow(0.97, k));
      setTick((t) => t + 1);
      return;
    }
    let alpha = 0.5;
    let frames = 0;
    const loop = () => {
      step(alpha);
      alpha *= 0.97;
      frames++;
      setTick((t) => t + 1);
      if (alpha > 0.02 && frames < 140) raf.current = requestAnimationFrame(loop);
    };
    raf.current = requestAnimationFrame(loop);
    return () => {
      if (raf.current) cancelAnimationFrame(raf.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, mounted, animate]);

  // Restrained perpetual drift (R7): a whisper-alpha sim tick + tiny per-node
  // sinusoidal nudges so the settled graph breathes instead of freezing —
  // terminal drift ≈2-3px/s (damping 0.82 caps it), throttled to ~30fps, free
  // in background tabs (RAF stops), paused per-node while that node is being
  // dragged, and skipped ENTIRELY under prefers-reduced-motion. The select
  // pulse rides this same loop via alphaBoost.
  useEffect(() => {
    if (!animate || !mounted || nodes.length === 0 || !pos.current[nodes[0].id]) return;
    if (layoutMode === 'orbit') return; // orbital layout is frozen — no drift
    let id: number;
    let last = 0;
    const loop = (now: number) => {
      id = requestAnimationFrame(loop);
      if (now - last < 33) return; // ~30fps is plenty for ambient motion
      last = now;
      const t = now / 1000;
      const m = pos.current;
      nodes.forEach((n, i) => {
        const p = m[n.id];
        if (!p || drag.current === n.id) return;
        const ph = i * 2.3999632; // golden-angle phase spread (no synchronized sway)
        p.vx += Math.cos(t * 0.55 + ph) * 0.016;
        p.vy += Math.sin(t * 0.45 + ph * 1.7) * 0.016;
      });
      step(0.018 + alphaBoost.current);
      alphaBoost.current *= 0.92;
      if (alphaBoost.current < 0.004) alphaBoost.current = 0;
      setTick((tk) => tk + 1);
    };
    id = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, mounted, animate]);

  const kick = () => setTick((t) => t + 1);
  /** Spring re-settle pulse on select (no-op under reduced motion — the drift
      loop that consumes the boost isn't running). */
  const pulse = () => {
    alphaBoost.current = 0.14;
  };
  return { pos: pos.current, drag, kick, pulse };
}

/* ── orbital layout (the deterministic evaluation prototype) ──────────────── */

/** What `useOrbitalLayout` returns when the mode is available. `pos` is a fresh
    map of node-id → fixed {x,y} (vx/vy are 0 — orbit mode never simulates). */
interface OrbitalLayout {
  /** True when ≥2 hubs exist (degree ≥ 5). When false the layout is unusable and
      the Orbit chip hides — force mode is used regardless of `layoutMode`. */
  available: boolean;
  /** Deterministic positions for EVERY drawn node (entities, guests, assets). */
  pos: Record<string, P>;
  /** The chosen hub ids — always labeled in orbit mode (orientation anchors). */
  hubIds: Set<string>;
  /** Rim floater ids — ALSO always labeled in orbit mode: rim islands sit so
      close their connecting line hides behind the avatars (PO feedback), and
      they're the long-tail entities nobody recognizes by logo alone. */
  rimIds: Set<string>;
  /**
   * The "NOT YET CONNECTED" band: the ellipse the zero-typed-edge members sit on.
   * The renderer strokes it faintly so that band reads as a holding pattern —
   * entities WAITING FOR EVIDENCE — rather than as debris around the edge of a
   * map (Lam 2026-09-04). Null when every member has at least one typed edge.
   */
  unconnectedRing: { cx: number; cy: number; rx: number; ry: number } | null;
}

/**
 * Deterministic ORBITAL layout (see the header doc-comment's ORBITAL LAYOUT
 * section for the full spec). Pure: identical for the same nodes/links/W/H. No
 * simulation — every position is computed exactly from the drawn graph, so the
 * caller can write the result straight into the renderer's `pos` map.
 *
 * Wedge/ring algorithm, in order:
 *   hubs → multi-source BFS home + hop → wedges proportional to membership →
 *   rings by hop → even angular spread (predecessor-sorted) with sub-ring split
 *   for crowded cohorts → floaters on an outer rim, GROUPED BY CONNECTED COMPONENT
 *   (consecutive, gap-separated, two interleaved rims when crowded) → asset
 *   satellites beside their parent. Assets/floaters never consume wedge slots.
 */
function useOrbitalLayout(nodes: GNode[], links: GLink[], W: number, H: number): OrbitalLayout {
  // Keyed on the TOPOLOGY, not the array identity: `nodes`/`links` are rebuilt
  // whenever the builder memo's inputs change (a new selection re-runs it), and
  // this whole deterministic layout would then recompute for an identical graph.
  // The id strings are the layout's real inputs.
  const nodeKey = nodes.map((n) => `${n.id}:${n.r}`).join('|');
  const linkKey = links.map((l) => `${l.source}>${l.target}${l.asset ? 'a' : ''}`).join('|');
  return useMemo<OrbitalLayout>(() => {
    const empty: OrbitalLayout = {
      available: false,
      pos: {},
      hubIds: new Set(),
      rimIds: new Set(),
      unconnectedRing: null,
    };
    if (nodes.length === 0 || W <= 0 || H <= 0) return empty;

    const cx = W / 2;
    const cy = H / 2;
    const R = Math.min(W, H);
    // ASPECT FILL (2026-09-04 audit §7): the polar layout is a DISC sized by the
    // SHORT canvas side, so a tall canvas wasted its long axis — the audit
    // measured a 351px-tall graph inside an 800px canvas, ~55% empty, with
    // fit-to-bounds then zooming out to the width. Stretching the long axis by
    // the canvas aspect (capped at 1.55 so the disc still reads as a disc, not a
    // smear) uses the whole frame and raises the fit zoom, which is what makes
    // the edge strokes and arrowheads legible.
    const ASPECT_CAP = 1.55;
    const sx = W >= H ? Math.min(ASPECT_CAP, W / H) : 1;
    const sy = H > W ? Math.min(ASPECT_CAP, H / W) : 1;
    const byId = new Map(nodes.map((n) => [n.id, n]));

    // Adjacency over the DRAWN links, but ONLY between ENTITY/GUEST nodes — asset
    // satellites are placed beside their parent afterward and must not pull other
    // nodes into a hub's BFS through themselves.
    const isEntityNode = (id: string) => byId.get(id)?.kind === 'entity';
    const adj = new Map<string, string[]>();
    const link = (x: string, y: string) => {
      const list = adj.get(x);
      if (list) list.push(y);
      else adj.set(x, [y]);
    };
    for (const l of links) {
      if (l.asset || !isEntityNode(l.source) || !isEntityNode(l.target)) continue;
      link(l.source, l.target);
      link(l.target, l.source);
    }

    // 1) HUBS — entity nodes with degree ≥ 5, sorted degree DESC then slug ASC,
    //    capped at 8. <2 hubs ⇒ orbital mode unavailable.
    const hubNodes = nodes
      .filter((n) => n.kind === 'entity' && n.deg >= 5)
      .sort((a, b) => b.deg - a.deg || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0))
      .slice(0, 8);
    if (hubNodes.length < 2) return empty;
    const hubIds = new Set(hubNodes.map((n) => n.id));
    const hubIndex = new Map(hubNodes.map((n, i) => [n.id, i]));

    // 2/3) Multi-source BFS over the entity graph: every reachable node gets a
    //      HOME hub (nearest; tie → earlier hub in sorted order) and a HOP count.
    //      Initialized with all hubs at hop 0, popped in hub-sorted order so ties
    //      resolve to the earlier hub deterministically.
    const home = new Map<string, number>(); // node id → hub INDEX
    const hop = new Map<string, number>();
    const pred = new Map<string, string | null>(); // BFS predecessor (for angle sort)
    const queue: string[] = [];
    hubNodes.forEach((h, i) => {
      home.set(h.id, i);
      hop.set(h.id, 0);
      pred.set(h.id, null);
      queue.push(h.id);
    });
    for (let qi = 0; qi < queue.length; qi++) {
      const cur = queue[qi];
      const curHome = home.get(cur)!;
      const curHop = hop.get(cur)!;
      for (const nb of adj.get(cur) ?? []) {
        if (home.has(nb)) continue; // first visit wins (shortest hop; hub-order tie)
        home.set(nb, curHome);
        hop.set(nb, curHop + 1);
        pred.set(nb, cur);
        queue.push(nb);
      }
    }

    // Group the REACHABLE non-hub members by home hub (for wedge sizing + layout).
    const membersByHub: string[][] = hubNodes.map(() => []);
    const floaters: GNode[] = []; // unreachable entities/guests (+ ALL-mode zero-edge)
    for (const n of nodes) {
      if (n.kind === 'asset') continue; // satellites placed separately
      if (hubIds.has(n.id)) continue; // hubs sit at their wedge center
      const h = home.get(n.id);
      if (h === undefined) floaters.push(n);
      else membersByHub[h].push(n.id);
    }

    // 2) WEDGES — width ∝ (1 + member count), normalized to 2π, in hub order from
    //    -π/2 (12 o'clock). Hub angle = its wedge center.
    const weights = membersByHub.map((m) => 1 + m.length);
    const totalW = weights.reduce((s, w) => s + w, 0);
    const wedgeStart: number[] = [];
    const wedgeWidth: number[] = [];
    let acc = -Math.PI / 2;
    for (let i = 0; i < hubNodes.length; i++) {
      const width = (weights[i] / totalW) * Math.PI * 2;
      wedgeStart[i] = acc;
      wedgeWidth[i] = width;
      acc += width;
    }
    const hubAngle = (i: number) => wedgeStart[i] + wedgeWidth[i] / 2;

    // RING radii by hop distance from the home hub. Tightened from the first cut
    // (0.18/0.40/0.56/0.70) — PO: "feels a bit too spread apart"; closer rings
    // also raise the fit-to-bounds zoom, which keeps edge strokes legible.
    const ringRadius = (h: number) =>
      h <= 0 ? 0.15 * R : h === 1 ? 0.3 * R : h === 2 ? 0.42 * R : 0.52 * R;

    const pos: Record<string, P> = {};
    const place = (id: string, angle: number, radius: number) => {
      pos[id] = {
        x: cx + Math.cos(angle) * radius * sx,
        y: cy + Math.sin(angle) * radius * sy,
        vx: 0,
        vy: 0,
      };
    };

    // Hubs: at their wedge-center angle, ring 0.
    hubNodes.forEach((h, i) => place(h.id, hubAngle(i), ringRadius(0)));

    // 4) ANGULAR PLACEMENT — per (hub, ring) cohort, sort by predecessor angle
    //    then slug, spread evenly across the wedge span inset by a margin. Split
    //    over-crowded cohorts into two interleaved sub-rings at r ± 14px.
    const WEDGE_INSET = 0.12; // radians trimmed off each wedge edge so slices breathe
    for (let i = 0; i < hubNodes.length; i++) {
      const members = membersByHub[i];
      if (members.length === 0) continue;
      // bucket this hub's members by hop ring
      const byRing = new Map<number, string[]>();
      for (const id of members) {
        const h = Math.min(3, hop.get(id) ?? 3);
        const list = byRing.get(h);
        if (list) list.push(id);
        else byRing.set(h, [id]);
      }
      const start = wedgeStart[i];
      const width = wedgeWidth[i];
      const usableStart = start + Math.min(WEDGE_INSET, width * 0.2);
      const usableWidth = Math.max(0.0001, width - 2 * Math.min(WEDGE_INSET, width * 0.2));
      // Process rings in ASCENDING hop order so a node's BFS predecessor (one hop
      // closer to the hub) is already placed when we read its angle for the sort.
      const ringsAsc = [...byRing.entries()].sort((a, b) => a[0] - b[0]);
      for (const [ring, ids] of ringsAsc) {
        const radius = ringRadius(ring);
        // sort by predecessor's already-placed angle (children near their parent),
        // then slug for a stable deterministic tiebreak.
        ids.sort((a, b) => {
          const pa = pred.get(a);
          const pb = pred.get(b);
          const aa = pa && pos[pa] ? Math.atan2(pos[pa].y - cy, pos[pa].x - cx) : hubAngle(i);
          const ab = pb && pos[pb] ? Math.atan2(pos[pb].y - cy, pos[pb].x - cx) : hubAngle(i);
          return aa - ab || (a < b ? -1 : a > b ? 1 : 0);
        });
        const n = ids.length;
        // crowding test: arc length per node along this ring vs node diameter+6.
        const arcPer = (usableWidth * radius) / Math.max(1, n);
        const diam = (byId.get(ids[0])?.r ?? 15) * 2 + 6;
        const split = n > 1 && arcPer < diam;
        ids.forEach((id, k) => {
          // evenly distribute across the usable span: centers of n equal slots.
          const t = n === 1 ? 0.5 : (k + 0.5) / n;
          const angle = usableStart + t * usableWidth;
          // split crowded cohorts into two interleaved sub-rings (alternate k).
          const r = split ? radius + (k % 2 === 0 ? -14 : 14) : radius;
          place(id, angle, r);
        });
      }
    }

    /* 5) FLOATERS — everything with no path to a hub, in TWO BANDS.

          ISLANDS      floaters that DO have typed edges, just not ones that reach
                       a hub (the GUNZ / Off The Grid / Gunzilla cluster). Evidence
                       exists. Inner rim, grouped by CONNECTED COMPONENT so island
                       partners sit side by side and their edges read as short arcs
                       instead of full-width chords; one empty GAP slot separates
                       components so islands read as distinct clumps.

          UNCONNECTED  zero typed edges at all — the entities WAITING FOR EVIDENCE.
                       Outer rim, grouped by entity TYPE so people / projects /
                       games / orgs read as sectors (gap slot between sectors), with
                       a faint ring stroked behind them. Before 2026-09-04 these
                       were simply absent from the default view, which read as "the
                       map is losing projects" — they are not clutter, they are the
                       backlog, and showing them is how growth becomes visible. */
    const floaterIds = new Set(floaters.map((n) => n.id));
    const hasTypedLink = (id: string) => (adj.get(id) ?? []).length > 0;
    const islands = floaters.filter((n) => hasTypedLink(n.id));
    const unconnected = floaters.filter((n) => !hasTypedLink(n.id));
    const islandIds = new Set(islands.map((n) => n.id));

    // Connected components of the ISLAND subgraph; member order = BFS visit order
    // from the component's smallest-slug member (neighbors visited slug-ascending).
    const bfsOrder = (root: string): string[] => {
      const order: string[] = [];
      const seen = new Set<string>([root]);
      const stack = [root];
      for (let si = 0; si < stack.length; si++) {
        const cur = stack[si];
        order.push(cur);
        const nbrs = (adj.get(cur) ?? [])
          .filter((nb) => islandIds.has(nb) && !seen.has(nb))
          .sort((a, b) => (a < b ? -1 : a > b ? 1 : 0));
        for (const nb of nbrs) {
          seen.add(nb);
          stack.push(nb);
        }
      }
      return order;
    };
    const visited = new Set<string>();
    const components: string[][] = [];
    for (const f of [...islands].sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0))) {
      if (visited.has(f.id)) continue;
      const order = bfsOrder(f.id);
      order.forEach((id) => visited.add(id));
      components.push(order);
    }
    // Order components: size DESC, then smallest-member-slug ASC. (Each component's
    // first member is already its smallest slug, since it was the BFS root.)
    components.sort((a, b) => b.length - a.length || (a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0));

    // RIM RADII — the inner rim sits just outside the ring the core ACTUALLY
    // reaches, then grows only until a slot is wide enough for a node. (A fixed
    // 0.62·R assumed a full four-ring core; a one- or two-hop core then stranded a
    // handful of floaters on a huge circle with a doughnut of dead canvas.)
    let maxRing = 0;
    for (const members of membersByHub)
      for (const id of members) maxRing = Math.max(maxRing, Math.min(3, hop.get(id) ?? 3));
    const sampleId = (islands[0] ?? unconnected[0])?.id;
    const sampleDiam = (byId.get(sampleId ?? '')?.r ?? 15) * 2 + 6;
    let innerRim = ringRadius(maxRing) + 0.09 * R;

    const innerSlots = islands.length + components.length;
    if (innerSlots > 0) {
      innerRim = Math.min(0.66 * R, Math.max(innerRim, (innerSlots * sampleDiam) / (2 * Math.PI)));
      // Crowding: if a slot is narrower than a node diameter+6, split into two
      // interleaved rims at innerRim ∓ 0.04·R — WHOLE components alternate.
      const crowded = islands.length > 1 && (2 * Math.PI * innerRim) / innerSlots < sampleDiam;
      let slot = 0;
      components.forEach((comp, ci) => {
        const rim = crowded ? innerRim + (ci % 2 === 0 ? -0.04 * R : 0.04 * R) : innerRim;
        comp.forEach((id, mi) => {
          const angle = (slot / innerSlots) * Math.PI * 2 - Math.PI / 2;
          // Radial ZIGZAG within a component (±10px alternating): adjacent island
          // partners would otherwise touch and their connecting line would vanish
          // behind the avatars — the stagger turns it into a visible diagonal.
          place(id, angle, rim + (mi % 2 === 0 ? -10 : 10));
          slot++;
        });
        slot++; // empty gap slot between components
      });
    }

    // OUTER RIM — the "not yet connected" band, grouped into TYPE SECTORS.
    let unconnectedRing: OrbitalLayout['unconnectedRing'] = null;
    if (unconnected.length > 0) {
      const SECTOR_ORDER = ['project', 'game', 'character', 'person', 'organization', 'protocol'];
      const rank = (t: string | undefined) => {
        const idx = SECTOR_ORDER.indexOf(t ?? '');
        return idx === -1 ? SECTOR_ORDER.length : idx;
      };
      const sorted = [...unconnected].sort(
        (a, b) =>
          rank(a.ent?.type) - rank(b.ent?.type) || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0),
      );
      const sectors: { rank: number; members: string[] }[] = [];
      for (const n of sorted) {
        const r = rank(n.ent?.type);
        const last = sectors[sectors.length - 1];
        if (last && last.rank === r) last.members.push(n.id);
        else sectors.push({ rank: r, members: [n.id] });
      }
      const outerSlots = unconnected.length + sectors.length;
      const outerRim = Math.max(
        innerRim + 0.1 * R,
        (outerSlots * sampleDiam) / (2 * Math.PI),
      );
      let slot = 0;
      for (const sec of sectors) {
        sec.members.forEach((id, mi) => {
          const angle = (slot / outerSlots) * Math.PI * 2 - Math.PI / 2;
          place(id, angle, outerRim + (mi % 2 === 0 ? -9 : 9));
          slot++;
        });
        slot++; // empty gap slot between type sectors
      }
      unconnectedRing = { cx, cy, rx: outerRim * sx, ry: outerRim * sy };
    }

    // 6) ASSET satellites — beside their first member parent (parent + 40px),
    //    angles spread evenly per parent so co-parented assets fan out. They do
    //    NOT consume wedge/ring slots (placed after everything else).
    const assetsByParent = new Map<string, GNode[]>();
    for (const n of nodes) {
      if (n.kind !== 'asset') continue;
      // first drawn parent that already has a position (asset edges are undirected)
      let parent: string | null = null;
      for (const l of links) {
        if (!l.asset) continue;
        const other = l.source === n.id ? l.target : l.target === n.id ? l.source : null;
        if (other && pos[other]) {
          parent = other;
          break;
        }
      }
      if (!parent) {
        // Parentless asset (shouldn't happen given the junction rule) — park it
        // on the outer rim deterministically so it never lacks a position.
        place(n.id, -Math.PI / 2, 0.85 * R);
        continue;
      }
      const list = assetsByParent.get(parent);
      if (list) list.push(n);
      else assetsByParent.set(parent, [n]);
    }
    for (const [parent, list] of assetsByParent) {
      const pp = pos[parent];
      if (!pp) continue;
      list.sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
      list.forEach((n, k) => {
        const angle = (k / list.length) * Math.PI * 2 - Math.PI / 2;
        pos[n.id] = { x: pp.x + Math.cos(angle) * 40, y: pp.y + Math.sin(angle) * 40, vx: 0, vy: 0 };
      });
    }

    return {
      available: true,
      pos,
      hubIds,
      rimIds: new Set(floaters.map((f) => f.id)),
      unconnectedRing,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodeKey, linkKey, W, H]);
}

/* ── component ───────────────────────────────────────────────────────────── */

export default function EntityGraph({
  entities,
  guests = [],
  typedEdges = [],
  assets = [],
  chainColor,
  chainLabel,
  selectedId = null,
  onSelectId,
  initialPath = null,
}: {
  entities: ChainEntity[];
  /** Off-chain GUEST nodes (server-projected, `offChain` = home chain id) —
      joined to the canvas by TYPED edges only; never in the directory/count. */
  guests?: ChainEntity[];
  /** Confirmed typed edges (server-fetched; every endpoint a member or an
      admitted guest, ≥1 endpoint always a member). */
  typedEdges?: ChainTypedEdge[];
  /** Linked-asset satellites (server-projected; parents are member slugs). */
  assets?: ChainAssetNode[];
  /** Chain identity color (the dot) — used as the protocol-node accent only. */
  chainColor: string;
  chainLabel: string;
  /** CONTROLLED selection (unified inspector): an entity SLUG or an asset node id
      (`asset:…`). When `onSelectId` is absent the graph falls back to local state. */
  selectedId?: string | null;
  onSelectId?: (id: string | null) => void;
  /** `?path=slugA,slugB` deep-link seed — opens path-finder mode between the two
      members (validated against the node set on mount). */
  initialPath?: readonly [string, string] | null;
}) {
  const isMobile = useIsMobile(1024);
  // <768px: the inspector becomes a fixed bottom sheet OVER the canvas.
  const isPhone = useIsMobile(768);
  // <820px: the shell's fixed bottom MobileNav exists (DashboardShell's
  // breakpoint). It overlays the canvas's bottom edge at common scroll
  // positions (landing: ~54px of canvas under it) and EATS taps on anything
  // pinned to the canvas bottom — so the zoom cluster + overlay legend get
  // nav-height bottom clearance at these widths (canvas CONTENT is pannable,
  // so nodes are never tap-trapped; only pinned controls needed rescuing).
  const hasBottomNav = useIsMobile(820);
  const overlayBottom = hasBottomNav ? 'calc(72px + env(safe-area-inset-bottom))' : 12;
  // Canvas height (Fork-2 Option A, Lam-approved 2026-06-10): VIEWPORT-DERIVED
  // on desktop. CSS drives the wrap — `calc(100dvh - 280px)` (≈ TopBar + page
  // header + graph header chrome) clamped to [560, 900] — and the SAME
  // ResizeObserver that measures W reads the result back into H state. Mobile
  // stays the fixed 420. Everything downstream (sim re-seed key, fit-to-bounds,
  // rail height) already re-keys on H; position-reuse keeps settled spots on
  // resize. 560 = the pre-measure default (the old desktop constant).
  const [H, setH] = useState(560);
  // Canvas WIDTH (measured). Declared up here beside H because the node builder
  // reads it: the ego seed count and the resting-label budget scale with the
  // canvas so a phone lands on a legible core instead of a desktop-sized one.
  const wrap = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const [W, setW] = useState(900);
  useEffect(() => {
    const ro = new ResizeObserver((es) => {
      for (const e of es) {
        setW(e.contentRect.width);
        // Height is CSS-driven (viewport clamp on desktop, 420 on mobile) —
        // read it back so the sim/viewBox/fit/rail all track the real canvas.
        if (e.contentRect.height > 0) setH(Math.round(e.contentRect.height));
      }
    });
    if (wrap.current) ro.observe(wrap.current);
    return () => ro.disconnect();
  }, []);
  const wrapHeightStyle: React.CSSProperties = isMobile
    ? { height: 420 }
    : { height: 'calc(100dvh - 280px)', minHeight: 560, maxHeight: 900 };
  // R7: gates ALL JS-driven nonessential motion (drift, pulse, smooth scroll);
  // CSS-driven motion is gated by the eg-motion-scope media query instead.
  const reducedMotion = usePrefersReducedMotion();

  // Client-only layout gate (avoids an SSR/hydration position mismatch — see useForce).
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  // "Assets" legend chip — 3-state cycle: shared junctions (default) → all → off.
  // Declared BEFORE the node builder: hidden assets are excluded from the sim
  // entirely (an invisible node still exerts forces — it would distort the
  // default layout), so the mode participates in topology.
  const [assetMode, setAssetMode] = useState<AssetMode>('shared');

  // "Layout" legend chip — the EVALUATION PROTOTYPE toggle: deterministic ORBIT
  // (default — what's being evaluated) ↔ organic FORCE (today's sim). Does NOT
  // touch topology (both modes draw the same nodes/links); it only chooses where
  // the renderer reads positions from. The chip auto-hides when <2 hubs exist
  // (orbital mode unavailable) and force mode is used regardless.
  const [layoutMode, setLayoutMode] = useState<LayoutMode>('orbit');

  // FOCUS — the ego core is a LENS, never a scope (Lam 2026-09-04: the collapsed
  // core "feels like we're losing projects/people").
  //
  // EVERY member is always on the canvas. What the ego selector now does is
  // HIGHLIGHT: with Focus on, the top-`seedCount` typed-degree entities and their
  // 1-hop neighbourhood stay lit and everything else drops to the 0.45 dim floor
  // — dimmed, never removed. Clicking a node does the same thing for its own
  // neighbourhood (the existing `neigh` axis). The map you can see is the whole
  // ecosystem; the map you can read is one chip away.
  const [focusCore, setFocusCore] = useState(false);

  // INFERRED LINKS — the untyped `related` co-occurrence fallback. OFF by default
  // (Lam 2026-09-04): on AVAX 63% of the drawn lines were these, rendered in the
  // same amber as verified relationships, which presented assumptions as the moat.
  // They stay one toggle away (Filters ▸ Evidence) and persist per viewer.
  const [showInferred, setShowInferred] = useState(false);
  useEffect(() => {
    // Per-viewer convenience only — a failed read/write must never break the map
    // (private windows and blocked site data throw on ACCESS, not just on read).
    try {
      if (window.localStorage.getItem(INFERRED_PREF_KEY) === '1') setShowInferred(true);
    } catch {
      /* storage unavailable — the default (off) stands */
    }
  }, []);
  const toggleInferred = useCallback(() => {
    setShowInferred((v) => {
      const next = !v;
      try {
        window.localStorage.setItem(INFERRED_PREF_KEY, next ? '1' : '0');
      } catch {
        /* storage unavailable — the toggle still works for this session */
      }
      return next;
    });
  }, []);

  // THE JUNCTION RULE: an asset earns canvas presence only when it connects ≥2
  // member entities. parentSlugs are already chain-member-intersected server-side;
  // the in-chain re-check is belt-and-braces.
  const junctionIds = useMemo(() => {
    const inChain = new Set(entities.map((e) => e.slug));
    return new Set(
      assets
        .filter((a) => a.parentSlugs.filter((s) => inChain.has(s)).length >= 2)
        .map((a) => a.id),
    );
  }, [entities, assets]);

  // -- ONE typed topology, consumed by EVERY core pass -----------------------
  // Eligibility (both endpoints member-or-guest, >=1 a member) then pair-dedupe
  // into degree / adjacency / bestConfidence / byPair. The drawn graph AND the
  // Core-vs-All chip's count both read THIS one structure: a second, independent
  // pass was how the chip could quietly disagree with the canvas about what the
  // core even is.
  const topo = useMemo(() => {
    const inChain = new Set(entities.map((e) => e.slug));
    const guestSlugs = new Set(guests.map((g) => g.slug));
    const ok = (sl: string) => inChain.has(sl) || guestSlugs.has(sl);
    const eligible = typedEdges.filter(
      (te) =>
        te.fromSlug !== te.toSlug &&
        ok(te.fromSlug) &&
        ok(te.toSlug) &&
        // GUESTS are 1-hop leaves: guest-to-guest never draws.
        (inChain.has(te.fromSlug) || inChain.has(te.toSlug)),
    );
    return { eligible, ...buildTypedTopology(eligible) };
  }, [entities, guests, typedEdges]);

  // Ego candidates - every member, plus the guests a typed edge actually reaches.
  // `contentCount` and `bestConfidence` ride along for the seed tie-breaks.
  const egoNodes = useMemo<EgoNode[]>(() => {
    const row = (e: ChainEntity): EgoNode => ({
      id: e.slug,
      typedDegree: topo.degree.get(e.slug) ?? 0,
      contentCount: e.contentCount ?? 0,
      bestConfidence: topo.bestConfidence.get(e.slug),
    });
    return [
      ...entities.map(row),
      ...guests.filter((g) => (topo.degree.get(g.slug) ?? 0) > 0).map(row),
    ];
  }, [entities, guests, topo]);

  // Seed count + label budget scale with the MEASURED canvas: 20 seeds is legible
  // at 787px and a smear at 348px (mobile review 2026-09-04, item 7).
  const seedCount = egoSeedCount(W);

  // Build nodes + (undirected, de-duped) edges.
  //
  // TYPED-ONLY + EGO DEFAULT (Lam 2026-09-04). The order matters:
  //   1  the shared topology sets every node's TYPED degree - the only degree
  //      that sizes a node or seeds the core.
  //   2  the VISIBLE set is chosen: core (ego network) or all.
  //   3  inferred `related` pairs are counted over the FULL member set (the
  //      header owes the reader the real number, not the number that survived
  //      the collapse) and drawn only when the viewer asks for them.
  //   4  asset satellites hang off visible parents.
  //
  // `typedByPair` carries the labels/narratives BESIDE the cheap force-sim links.
  // `hiddenByEntity` counts each entity's OFF-canvas portfolio for the diamond badge.
  const {
    nodes,
    links,
    typedByPair,
    hiddenByEntity,
    typedRelCount,
    typedRelTotal,
    inferredCount,
    unconnectedCount,
    memberNodeCount,
    seedIds,
  } = useMemo(() => {
    const inChain = new Set(entities.map((e) => e.slug));
    const bySlug = new Map([...entities, ...guests].map((e) => [e.slug, e]));
    const edgeKeys = new Set<string>();
    const links: GLink[] = [];
    const typedByPair = topo.byPair;
    const deg: Record<string, number> = {};
    const bump = (a: string, b: string) => {
      deg[a] = (deg[a] || 0) + 1;
      deg[b] = (deg[b] || 0) + 1;
    };

    /* 1) THE VISIBLE SET = EVERYONE. Every directory member draws, always; the
          ego core is a highlight (see `focusIds`), not a filter. Guests are the
          one exception and always were: an off-chain node earns the canvas only
          through the typed edge that reached it, so a guest with no visible
          member endpoint would be a stranded dot. */
    const seedIds = egoSeedIds(egoNodes, seedCount);
    const visible = new Set(egoNodes.map((n) => n.id));
    for (const g of guests) {
      if (!visible.has(g.slug)) continue;
      const anchored = (topo.adjacency.get(g.slug) ?? []).some((nb) => visible.has(nb));
      if (!anchored) visible.delete(g.slug);
    }

    // 2) Draw the typed links whose BOTH endpoints survived.
    for (const p of topo.pairs) {
      if (!visible.has(p.source) || !visible.has(p.target)) continue;
      edgeKeys.add(pairKey(p.source, p.target));
      links.push({ source: p.source, target: p.target, typed: true });
      bump(p.source, p.target);
    }

    /* 3) INFERRED (untyped `related`) pairs - every pair with no typed edge and
          both endpoints in-chain. COUNTED over the FULL member set (the header
          must declare what exists, not what happened to survive the ego collapse);
          DRAWN only when `showInferred` AND both ends are on the canvas. */
    let inferredCount = 0;
    const inferredSeen = new Set<string>();
    for (const e of entities) {
      for (const r of e.related) {
        if (r.slug === e.slug || !inChain.has(r.slug)) continue;
        const k = pairKey(e.slug, r.slug);
        if (typedByPair.has(k) || inferredSeen.has(k)) continue; // typed wins; de-dupe
        inferredSeen.add(k);
        inferredCount++;
        if (!showInferred || !visible.has(e.slug) || !visible.has(r.slug)) continue;
        edgeKeys.add(k);
        links.push({ source: e.slug, target: r.slug, typed: false });
        bump(e.slug, r.slug);
      }
    }

    /* 4) ASSET satellites - ONLY the current mode's visible subset joins the
          graph/sim (shared = junctions, all = every satellite, none = []); a
          dotted edge from each visible asset to every VISIBLE member parent.
          (Identity-vs-member de-dupe already happened server-side.) */
    const modeAssets =
      assetMode === 'all'
        ? assets
        : assetMode === 'shared'
          ? assets.filter((a) => junctionIds.has(a.id))
          : [];
    const assetIds = new Map<string, ChainAssetNode>();
    for (const a of modeAssets) {
      const parents = a.parentSlugs.filter((ps) => inChain.has(ps) && visible.has(ps));
      if (parents.length === 0) continue; // its parents were collapsed - no orphan diamonds
      assetIds.set(a.id, a);
      for (const ps of parents) {
        const k = pairKey(a.id, ps);
        if (edgeKeys.has(k)) continue;
        edgeKeys.add(k);
        links.push({ source: a.id, target: ps, typed: false, asset: true });
        bump(a.id, ps);
      }
    }

    // Diamond-badge counts - the entity's portfolio assets NOT on the canvas in
    // this mode (identity tokens excluded: they are the header $SYM chip).
    const hiddenByEntity = new Map<string, number>();
    for (const a of assets) {
      if (assetIds.has(a.id)) continue;
      for (const ps of a.parentSlugs) {
        const e = bySlug.get(ps);
        if (!e || !inChain.has(ps) || isIdentityTokenOf(e, a)) continue;
        hiddenByEntity.set(ps, (hiddenByEntity.get(ps) ?? 0) + 1);
      }
    }

    const ids = new Set<string>([...visible, ...assetIds.keys()]);
    const nodes: GNode[] = [...ids].map((id) => {
      const a = assetIds.get(id);
      if (a) return { id, kind: 'asset', asset: a, deg: deg[id] || 1, typedDeg: 0, r: 10 };
      const ent = bySlug.get(id)!;
      const td = topo.degree.get(id) ?? 0;
      return {
        id,
        kind: 'entity',
        ent,
        deg: deg[id] || 1,
        typedDeg: td,
        // RADIUS = TYPED degree (+ a whisper of coverage). Never the drawn degree
        // - untyped co-occurrences and asset satellites used to inflate it.
        r: nodeRadius(td, ent?.contentCount ?? 0),
      };
    });

    const memberNodeCount = [...visible].filter((id) => inChain.has(id)).length;
    return {
      nodes,
      links,
      typedByPair,
      hiddenByEntity,
      // Typed RELATIONSHIPS (edge docs, not pairs): on the canvas, and in total.
      typedRelCount: topo.eligible.filter(
        (te) => visible.has(te.fromSlug) && visible.has(te.toSlug),
      ).length,
      typedRelTotal: topo.eligible.length,
      inferredCount,
      // MEMBERS WITH NO TYPED EDGE — the honest "waiting for evidence" figure the
      // header declares and the outer rim draws. Not hidden, just not yet joined.
      unconnectedCount: entities.filter((e) => (topo.degree.get(e.slug) ?? 0) === 0).length,
      memberNodeCount,
      seedIds,
    };
  }, [entities, guests, topo, egoNodes, seedCount, assets, assetMode, junctionIds, showInferred]);

  // Slug → entity over members AND guests (guests must resolve for the node
  // renderer, edge type-dim checks, the inspector and rail relationship rows).
  const bySlug = useMemo(
    () => new Map([...entities, ...guests].map((e) => [e.slug, e])),
    [entities, guests],
  );
  const assetById = useMemo(() => new Map(assets.map((a) => [a.id, a])), [assets]);

  // Core/All counts for the scope chip. `coreCount` is computed INDEPENDENTLY of
  // the active scope (a second, cheap ego pass over ≤200 ids) so the chip can
  // read "Core · 24" while you are looking at All — the label must name what the
  // OTHER state holds, not echo the one you're in.
  // THE FOCUS SET — the ego core, used as a HIGHLIGHT axis rather than a filter.
  // Same function, same topology as everything else that reasons about the core.
  const focusIds = useMemo(
    () => selectEgoCore(egoNodes, topo.adjacency, seedCount),
    [egoNodes, topo, seedCount],
  );
  const focusCount = useMemo(() => {
    const inChain = new Set(entities.map((e) => e.slug));
    return [...focusIds].filter((id) => inChain.has(id)).length;
  }, [entities, focusIds]);

  // EDGE MODE (map-v2.html "edge as first-class"): a selected edge PAIR-KEY (or
  // null). When set, the inspector flips to EDGE mode (A —TYPE→ B + narrative +
  // confidence + sources count). Promoted by clicking an edge midpoint OR a
  // relationship row in the node inspector; cleared by Esc/× or selecting a node.
  const [edgePairKey, setEdgePairKey] = useState<string | null>(null);

  // SENTIMENT lens (map-v2.html) — a COLOR OVERLAY (not a dim filter): when on,
  // node rings tint by `recon.sentiment` (bullish=green, bearish=red, neutral/
  // none=slate-dim). Independent boolean so it COMPOSES with every other axis
  // (type/lens/off-chain/path) instead of being a mutually-exclusive lens.
  const [sentimentOn, setSentimentOn] = useState(false);

  // PATH FINDER (map-v2.html "how are X and Y connected") — a small state machine:
  //   • pathMode false        → off (default).
  //   • pathMode, ends []      → PICKING: click node A then node B (microcopy guides).
  //   • pathMode, ends [a]     → A chosen, awaiting B.
  //   • pathMode, ends [a,b]   → BFS resolved (or "no connection") — path lit, rest dim.
  // Deep-linkable via ?path=a,b (seeded below). Path is a LENS, not a filter: it
  // keeps its own exit (the microcopy's ×) and its own `Lens •` indicator, and is
  // deliberately outside `Reset view` so that button's promise stays exactly the
  // `Filters · N` badge (review item 5).
  const [pathMode, setPathMode] = useState(false);
  const [pathEnds, setPathEnds] = useState<string[]>([]);
  // Seed from the ?path= deep-link ONCE on mount (only if both slugs are members).
  const pathSeeded = useRef(false);
  useEffect(() => {
    if (pathSeeded.current || !initialPath) return;
    pathSeeded.current = true;
    const [a, b] = initialPath;
    const inChain = new Set(entities.map((e) => e.slug));
    if (inChain.has(a) && inChain.has(b) && a !== b) {
      setPathMode(true);
      setPathEnds([a, b]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialPath]);

  // CONTROL-BAR DROPDOWNS (2026-06-12 PO declutter) — the old bottom-left bar
  // crammed ~12 chips into one wrapping row; it now collapses into two popover
  // buttons ("Filter ▾" / "Lens ▾"). Exactly one menu is open at a time, so a
  // single nullable selector drives both. `null` = both closed. The Layout chip
  // graduated out of this bar entirely (it lives in the canvas top-left corner).
  const [openMenu, setOpenMenu] = useState<'filter' | 'lens' | null>(null);

  // SENTIMENT chip shows only when ≥1 member carries recon to tint by (else the
  // overlay would be all-neutral — nothing to read).
  const hasRecon = useMemo(() => entities.some((e) => !!e.recon?.sentiment), [entities]);

  // PATH FINDER BFS (map-v2.html) — shortest path between the two chosen ends over
  // the DRAWN graph (every link: typed, generic, AND asset satellites — the user
  // asks "how are these connected" of whatever the canvas shows). Resolves only
  // when both ends are picked. Returns the ordered node slugs + the pair-keys of
  // the hop edges (for lighting), or { found:false } when no connection exists.
  const pathResult = useMemo<{
    found: boolean;
    nodes: string[];
    edgeKeys: Set<string>;
  } | null>(() => {
    if (pathEnds.length < 2) return null;
    const [a, b] = pathEnds;
    if (a === b) return { found: true, nodes: [a], edgeKeys: new Set() };
    // adjacency over the drawn links
    const adj = new Map<string, string[]>();
    const link = (x: string, y: string) => {
      const list = adj.get(x);
      if (list) list.push(y);
      else adj.set(x, [y]);
    };
    for (const l of links) {
      link(l.source, l.target);
      link(l.target, l.source);
    }
    const prev = new Map<string, string | null>([[a, null]]);
    const queue = [a];
    let found = false;
    while (queue.length) {
      const cur = queue.shift()!;
      if (cur === b) {
        found = true;
        break;
      }
      for (const nb of adj.get(cur) ?? []) {
        if (!prev.has(nb)) {
          prev.set(nb, cur);
          queue.push(nb);
        }
      }
    }
    if (!found) return { found: false, nodes: [], edgeKeys: new Set() };
    // reconstruct a→b
    const chain: string[] = [];
    for (let cur: string | null = b; cur !== null; cur = prev.get(cur) ?? null) chain.push(cur);
    chain.reverse();
    const edgeKeys = new Set<string>();
    for (let i = 0; i < chain.length - 1; i++) edgeKeys.add(pairKey(chain[i], chain[i + 1]));
    return { found: true, nodes: chain, edgeKeys };
  }, [pathEnds, links]);
  // The set of node slugs ON the resolved path (the lit set under path mode). null
  // when path mode isn't actively constraining the view (off, or only A picked —
  // in which case only the picked end + everything stays interactive for B).
  const pathLit = useMemo<Set<string> | null>(() => {
    if (!pathMode) return null;
    if (pathResult?.found) return new Set(pathResult.nodes);
    return null; // picking, or no-path → no path-dim (don't blank the canvas)
  }, [pathMode, pathResult]);
  // If the active path's chosen ends drop out of the drawn graph (a filter/toggle
  // removed a node), clear the pair so the inspector never strands on a dead path.
  useEffect(() => {
    if (pathEnds.length === 0) return;
    const present = new Set(nodes.map((n) => n.id));
    if (pathEnds.some((s) => !present.has(s))) setPathEnds([]);
  }, [nodes, pathEnds]);

  // Deterministic ORBITAL positions (the evaluation prototype). Computed from the
  // same drawn graph; `available` is false (<2 hubs) → the chip hides + force is
  // forced. `effectiveLayout` is the mode actually applied (orbit only when usable).
  const orbital = useOrbitalLayout(nodes, links, W, H);
  const effectiveLayout: LayoutMode =
    layoutMode === 'orbit' && orbital.available ? 'orbit' : 'force';

  const { pos, drag, kick, pulse } = useForce(
    nodes,
    links,
    W,
    H,
    mounted,
    !reducedMotion,
    effectiveLayout,
    orbital.pos,
  );

  // The most-connected ENTITY nodes always keep their label (orientation anchors).
  // In ORBIT mode the layout's chosen hubs (the wedge anchors) are ALSO always
  // labeled — they're the same top-degree set, but unioning keeps them in lock-
  // step with the layout even if the tie-break ever diverges. (Declared here,
  // after the orbital layout, so it can union in the orbital hub set.)
  const hubIds = useMemo(() => {
    const s = new Set(
      nodes
        .filter((n) => n.kind === 'entity')
        .sort((a, b) => b.deg - a.deg)
        .slice(0, 8)
        .map((n) => n.id),
    );
    if (effectiveLayout === 'orbit') for (const id of orbital.hubIds) s.add(id);
    return s;
  }, [nodes, effectiveLayout, orbital.hubIds]);

  // RESTING LABELS (review 2026-09-04 item 8). The old rule named orbit hubs and
  // EVERY rim floater: 159 colliding texts on Abstract, while the core's own
  // leaves stayed anonymous - a map of unnamed avatars. The rule now (see
  // `restingLabelIds`): every node with typedDegree >= 2, every ego seed, then
  // the remainder by degree until the width-scaled budget is spent. Rules 1-2 are
  // unconditional, so a dense map degrades by dropping its least-connected
  // labels, never its spine.
  const restingLabels = useMemo(() => {
    const rows: EgoNode[] = nodes
      .filter((n) => n.kind === 'entity')
      .map((n) => ({
        id: n.id,
        typedDegree: n.typedDeg,
        contentCount: n.ent?.contentCount ?? 0,
        bestConfidence: topo.bestConfidence.get(n.id),
      }));
    const labels = restingLabelIds(rows, seedIds, labelBudget(W));
    // ORBIT rim islands: partners sit nearly touching (their line hides behind
    // the avatars) and they are the long-tail entities a logo alone doesn't
    // identify - so keep naming them, but CAPPED by canvas width.
    if (effectiveLayout === 'orbit' && orbital.rimIds.size > 0) {
      const cap = rimLabelCap(W);
      const rim = rows
        .filter((r) => orbital.rimIds.has(r.id))
        .sort((a, b) => b.typedDegree - a.typedDegree || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0))
        .slice(0, cap);
      for (const r of rim) labels.add(r.id);
    }
    for (const id of hubIds) labels.add(id);
    return labels;
  }, [nodes, topo, seedIds, W, effectiveLayout, orbital.rimIds, hubIds]);

  // Selection — CONTROLLED by the page-level explorer (unified inspector) when
  // `onSelectId` is provided; local state is only the standalone fallback.
  const [localSelected, setLocalSelected] = useState<string | null>(null);
  const selected = onSelectId ? selectedId : localSelected;
  const setSelected = onSelectId ?? setLocalSelected;
  // Selecting a NODE (or clearing selection) always exits EDGE mode — the two
  // inspector modes are mutually exclusive. Defined here so node/asset/rail
  // clicks route through it; edge clicks call setEdgePairKey directly.
  const selectNode = useCallback(
    (id: string | null) => {
      setEdgePairKey(null);
      setSelected(id);
    },
    [setSelected],
  );

  // PATH PICKING router: while path mode is collecting endpoints, a node click
  // picks A then B instead of selecting into the inspector. Once both are picked
  // (path resolved), clicks return to normal selection. Picking a brand-new pair
  // restarts. Entity nodes only — asset satellites aren't path endpoints.
  const onNodeClickRouted = useCallback(
    (id: string, kind: 'entity' | 'asset') => {
      if (pathMode && kind === 'entity' && pathEnds.length < 2) {
        setPathEnds((ends) => {
          if (ends.length === 0) return [id];
          if (ends[0] === id) return ends; // ignore a double-tap on A
          return [ends[0], id];
        });
        return;
      }
      selectNode(id);
    },
    [pathMode, pathEnds, selectNode],
  );

  // Spring re-settle pulse on select (R7): a gentle alpha kick so the chosen
  // node's neighbourhood visibly re-seats. No-op under reduced motion.
  useEffect(() => {
    if (selected) pulse();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  const [hover, setHover] = useState<string | null>(null);
  // Type filter driven by the (clickable) legend — dims nodes not of the chosen type(s).
  const [typeFilter, setTypeFilter] = useState<string[] | null>(null);
  // Off-chain-guests filter (independent of typeFilter, like the Assets chip) —
  // dims every on-chain entity, leaving only off-chain guest nodes lit.
  const [guestsOnly, setGuestsOnly] = useState(false);
  const isTypeDimmed = (t: string) => !!typeFilter && !typeFilter.includes(t);
  // PATH mode dim: when a path is RESOLVED, every node NOT on it dims (the existing
  // dim path, reused per spec). Picking/no-path → pathLit is null → no path-dim.
  const isPathDimmed = (id: string | undefined) => !!pathLit && (!id || !pathLit.has(id));
  // An entity node matches iff it passes the type filter AND (when guestsOnly is
  // on) is itself an off-chain guest AND (when a path is resolved) it sits on the
  // path — non-matching entities dim. Every axis composes INDEPENDENTLY via the
  // SAME dim path. Reused for nodes and for an edge's entity endpoints so both
  // share one dim definition.
  // FOCUS dim: with the Focus lens on, anything outside the ego core drops to the
  // dim floor. It composes with every other axis and REMOVES NOTHING.
  const isFocusDimmed = (slug?: string) => focusCore && !!slug && !focusIds.has(slug);
  const isEntityDimmed = (e: { type: string; offChain?: unknown; slug?: string } | undefined) =>
    !e ||
    isTypeDimmed(e.type) ||
    (guestsOnly && !e.offChain) ||
    isFocusDimmed(e.slug) ||
    isPathDimmed(e.slug);
  // An asset node dims when a type filter is on and NONE of its parents match it,
  // OR whenever guestsOnly is on (assets are never off-chain guests).
  const isAssetDimmed = (a: ChainAssetNode) =>
    guestsOnly ||
    isPathDimmed(`asset:${a.assetType}_${a.assetId.toLowerCase()}`) ||
    (!!typeFilter &&
      !a.parentSlugs.some((s) => {
        const e = bySlug.get(s);
        return !!e && typeFilter.includes(e.type);
      }));
  const [view, setView] = useState({ x: 0, y: 0, k: 1 });
  const viewRef = useRef(view);
  viewRef.current = view;

  // pointer interaction: drag a node, pan the background, OR pinch with 2 fingers.
  // Disambiguation: single touch on a node = drag node; single touch on canvas =
  // pan; the SECOND finger landing anywhere promotes the gesture to pinch-zoom.
  const action = useRef<
    | null
    | { mode: 'node'; id: string }
    | { mode: 'pan'; lastX: number; lastY: number; moved: boolean }
    | { mode: 'pinch'; lastD: number; lastMX: number; lastMY: number }
  >(null);
  // Live positions of every active pointer (touch fingers / mouse), client coords.
  const pointers = useRef(new Map<number, { x: number; y: number }>());
  // Deadline (ms epoch) until which the synthetic click trailing a pan/pinch is
  // ignored (it must not deselect). TIME-BASED, not a boolean: the old sticky
  // flag was only cleared by the next background click, so if the trailing click
  // landed on a node instead it stayed armed forever and silently ate a future
  // dismissal tap. A ~300ms self-expiry makes that leak impossible.
  const suppressBgClickUntil = useRef(0);

  const screenToWorld = (clientX: number, clientY: number) => {
    const rect = svgRef.current!.getBoundingClientRect();
    const sx = W / rect.width;
    const sy = H / rect.height;
    const vx = (clientX - rect.left) * sx;
    const vy = (clientY - rect.top) * sy;
    const v = viewRef.current;
    return { x: (vx - v.x) / v.k, y: (vy - v.y) / v.k };
  };

  /** Client coords → viewBox coords (pre-view-transform). */
  const screenToCanvas = (clientX: number, clientY: number) => {
    const rect = svgRef.current!.getBoundingClientRect();
    return { x: (clientX - rect.left) * (W / rect.width), y: (clientY - rect.top) * (H / rect.height) };
  };

  /** When a 2nd pointer lands, promote whatever gesture is active to a pinch. */
  const maybeStartPinch = () => {
    if (pointers.current.size !== 2 || !svgRef.current) return;
    const [p1, p2] = [...pointers.current.values()];
    const a = screenToCanvas(p1.x, p1.y);
    const b = screenToCanvas(p2.x, p2.y);
    drag.current = null; // a node drag in progress yields to the pinch
    action.current = {
      mode: 'pinch',
      lastD: Math.max(12, Math.hypot(b.x - a.x, b.y - a.y)),
      lastMX: (a.x + b.x) / 2,
      lastMY: (a.y + b.y) / 2,
    };
  };

  useEffect(() => {
    const move = (e: globalThis.PointerEvent) => {
      const tracked = pointers.current.get(e.pointerId);
      if (tracked) {
        tracked.x = e.clientX;
        tracked.y = e.clientY;
      }
      const it = action.current;
      if (!it) return;
      if (it.mode === 'node') {
        const w = screenToWorld(e.clientX, e.clientY);
        const p = pos[it.id];
        if (p) {
          p.x = w.x;
          p.y = w.y;
          p.vx = 0;
          p.vy = 0;
          kick();
        }
      } else if (it.mode === 'pan') {
        const rect = svgRef.current!.getBoundingClientRect();
        const sx = W / rect.width;
        const sy = H / rect.height;
        const dx = (e.clientX - it.lastX) * sx;
        const dy = (e.clientY - it.lastY) * sy;
        it.lastX = e.clientX;
        it.lastY = e.clientY;
        it.moved = true;
        setView((v) => ({ ...v, x: v.x + dx, y: v.y + dy }));
      } else if (it.mode === 'pinch' && pointers.current.size >= 2) {
        const [p1, p2] = [...pointers.current.values()];
        const a = screenToCanvas(p1.x, p1.y);
        const b = screenToCanvas(p2.x, p2.y);
        const d = Math.max(12, Math.hypot(b.x - a.x, b.y - a.y));
        const mx = (a.x + b.x) / 2;
        const my = (a.y + b.y) / 2;
        const factor = d / it.lastD;
        setView((v) => {
          const k2 = Math.min(3, Math.max(0.4, v.k * factor));
          // zoom about the current midpoint, then follow the midpoint's travel (pan)
          const x = mx - (mx - v.x) * (k2 / v.k) + (mx - it.lastMX);
          const y = my - (my - v.y) * (k2 / v.k) + (my - it.lastMY);
          return { k: k2, x, y };
        });
        it.lastD = d;
        it.lastMX = mx;
        it.lastMY = my;
      }
    };
    const up = (e: globalThis.PointerEvent) => {
      pointers.current.delete(e.pointerId);
      const it = action.current;
      if (it && (it.mode === 'pinch' || (it.mode === 'pan' && it.moved))) {
        suppressBgClickUntil.current = Date.now() + 300; // the trailing click must not deselect
      }
      if (it?.mode === 'pinch' && pointers.current.size >= 2) return; // still pinching
      drag.current = null;
      action.current = null;
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);
    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', up);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pos, W, H]);

  // wheel zoom (non-passive so we can preventDefault the page scroll).
  useEffect(() => {
    const el = svgRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const rect = el.getBoundingClientRect();
      const fx = (e.clientX - rect.left) * (W / rect.width);
      const fy = (e.clientY - rect.top) * (H / rect.height);
      setView((v) => {
        const k2 = Math.min(3, Math.max(0.4, v.k * (e.deltaY < 0 ? 1.12 : 1 / 1.12)));
        return { k: k2, x: fx - (fx - v.x) * (k2 / v.k), y: fy - (fy - v.y) * (k2 / v.k) };
      });
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [W, H]);

  const zoomBy = (factor: number) =>
    setView((v) => {
      const k2 = Math.min(3, Math.max(0.4, v.k * factor));
      const fx = W / 2;
      const fy = H / 2;
      return { k: k2, x: fx - (fx - v.x) * (k2 / v.k), y: fy - (fy - v.y) * (k2 / v.k) };
    });

  // Frame the whole settled graph in the canvas (used on load + the "Fit" button).
  // Fixes desktop crowding feel and mobile right-edge clipping in one move.
  const fitToBounds = useCallback(() => {
    if (nodes.length === 0) return;
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const n of nodes) {
      const p = pos[n.id];
      if (!p) continue;
      minX = Math.min(minX, p.x - n.r);
      maxX = Math.max(maxX, p.x + n.r);
      minY = Math.min(minY, p.y - n.r);
      maxY = Math.max(maxY, p.y + n.r);
    }
    // Frame the "not yet connected" ring too — its caption sits ~22px above the
    // topmost rim node, so fitting to nodes alone clipped the band's own label.
    const ring = effectiveLayout === 'orbit' ? orbital.unconnectedRing : null;
    if (ring) {
      minX = Math.min(minX, ring.cx - ring.rx);
      maxX = Math.max(maxX, ring.cx + ring.rx);
      minY = Math.min(minY, ring.cy - ring.ry - 22);
      maxY = Math.max(maxY, ring.cy + ring.ry);
    }
    if (!Number.isFinite(minX)) return;
    const pad = 30;
    const bw = maxX - minX + pad * 2;
    const bh = maxY - minY + pad * 2;
    const k = Math.min(2.2, Math.max(0.45, Math.min(W / bw, H / bh)));
    const cx = (minX + maxX) / 2;
    const cy = (minY + maxY) / 2;
    setView({ k, x: W / 2 - cx * k, y: H / 2 - cy * k });
  }, [nodes, pos, W, H, effectiveLayout, orbital.unconnectedRing]);

  // One-shot fit whenever the topology/size (and thus the layout) changes.
  const fittedKey = useRef('');
  useEffect(() => {
    if (!mounted || nodes.length === 0 || !pos[nodes[0].id]) return;
    // The layout mode is folded in so flipping Orbit↔Force re-frames the (now
    // differently-shaped) graph, matching the sim/fit re-key on every other
    // topology change.
    const fk = `${nodes.map((n) => n.id).join('|')}@${Math.round(W)}x${H}#${effectiveLayout}`;
    if (fittedKey.current === fk) return;
    fittedKey.current = fk;
    fitToBounds();
  }, [mounted, nodes, W, H, pos, fitToBounds, effectiveLayout]);

  const onNodeDown = (id: string, e: ReactPointerEvent) => {
    e.stopPropagation();
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.current.size >= 2) {
      maybeStartPinch();
      return;
    }
    drag.current = id;
    action.current = { mode: 'node', id };
  };
  const onBgDown = (e: ReactPointerEvent) => {
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.current.size >= 2) {
      maybeStartPinch();
      return;
    }
    action.current = { mode: 'pan', lastX: e.clientX, lastY: e.clientY, moved: false };
  };
  const onBgClick = () => {
    // a plain click on the canvas clears the selection (and any edge mode); the
    // synthetic click that trails a pan/pinch is suppressed (a self-expiring
    // deadline set on pointerup).
    if (Date.now() < suppressBgClickUntil.current) return;
    selectNode(null);
  };

  // neighbor set for the focus highlight (selected wins over hover)
  const focus = selected || hover;
  const neigh = useMemo(() => {
    if (!focus) return null;
    const s = new Set<string>([focus]);
    links.forEach((l) => {
      if (l.source === focus) s.add(l.target);
      if (l.target === focus) s.add(l.source);
    });
    return s;
  }, [focus, links]);

  // Entity-type ring color. Single-sourced in graph-style.ts — project / game /
  // character used to fall through to `var(--divider)` (#2A2A2A), a hairline that
  // reads as nothing, so most of every graph was undifferentiated avatar circles
  // (audit 2026-09-04 §4). `protocol` still wears the chain's identity color.
  const typeStroke = (t: string) => typeRingColor(t, chainColor);

  // Per-link stroke recipe from the ontology (family → solid/dashed + arrow
  // direction, confidence → alpha). Memoized off the drawn links so panning and
  // zooming don't re-derive it.
  const edgeStyles = useMemo(() => {
    const m = new Map<string, ReturnType<typeof typedEdgeStyle>>();
    for (const l of links) {
      if (!l.typed || l.asset) continue;
      const k = pairKey(l.source, l.target);
      if (m.has(k)) continue;
      m.set(k, typedEdgeStyle(typedByPair.get(k) ?? [], l.source, l.target));
    }
    return m;
  }, [links, typedByPair]);

  /** Node radius by id — the arrowhead has to stop at the node's edge, not its center. */
  const radiusById = useMemo(() => new Map(nodes.map((n) => [n.id, n.r])), [nodes]);

  // SENTIMENT overlay (map-v2.html): map `recon.sentiment` → a ring tint. bullish =
  // signal-green, bearish = alert-red, everything else (neutral / informational /
  // no recon) = neutral-dim slate. A COLOR OVERLAY: returned only when sentimentOn,
  // and selection still wins (the ring stays amber for the selected node).
  const sentimentRing = (e: ChainEntity | undefined): string | null => {
    if (!sentimentOn) return null;
    const s = e?.recon?.sentiment;
    if (s === 'bullish') return 'var(--green-text)';
    if (s === 'bearish') return 'var(--red-text)';
    return 'var(--slate)'; // neutral / informational / no recon → neutral-dim
  };

  const selectedEntity = selected ? bySlug.get(selected) ?? null : null;
  const selectedAsset = selected ? assetById.get(selected) ?? null : null;

  // The selected node's typed edges, resolved from ITS perspective for the rail
  // (label + the other endpoint + the narrative description — the product).
  const selectedRels = useMemo<RailRelationship[]>(() => {
    if (!selected) return [];
    const out: RailRelationship[] = [];
    for (const te of typedEdges) {
      const otherSlug: string | null =
        te.fromSlug === selected ? te.toSlug : te.toSlug === selected ? te.fromSlug : null;
      if (!otherSlug || otherSlug === selected) continue;
      const other = bySlug.get(otherSlug);
      if (!other) continue;
      out.push({
        label: labelFrom(te, selected),
        other,
        description: te.description,
        confidence: te.confidence,
      });
    }
    return out;
  }, [selected, typedEdges, bySlug]);

  // EDGE MODE payload (edge-as-first-class): the typed edge(s) on the selected
  // pair, resolved to endpoint entities + narrative + confidence + provenance
  // count. A pair may carry several typed edges (e.g. founded + worked-at) — all
  // render. Endpoints fixed to the stored direction so the A —TYPE→ B header reads
  // the edge's real orientation.
  const selectedEdges = useMemo<RailEdge[]>(() => {
    if (!edgePairKey) return [];
    const tes = typedByPair.get(edgePairKey);
    if (!tes || tes.length === 0) return [];
    const out: RailEdge[] = [];
    for (const te of tes) {
      const from = bySlug.get(te.fromSlug);
      const to = bySlug.get(te.toSlug);
      if (!from || !to) continue;
      out.push({
        from,
        to,
        label: te.forwardLabel,
        type: te.type,
        description: te.description,
        confidence: te.confidence,
        sourceCount: te.sourceCount,
        sourceHosts: te.sourceHosts ?? [],
        role: te.role,
        startDate: te.startDate ?? null,
        createdAt: te.createdAt,
      });
    }
    return out;
  }, [edgePairKey, typedByPair, bySlug]);
  // If the active edge vanished (its pair no longer drawn — e.g. a filter), drop
  // edge mode so the inspector never strands on a dead edge.
  useEffect(() => {
    if (edgePairKey && (!typedByPair.has(edgePairKey) || selectedEdges.length === 0)) {
      setEdgePairKey(null);
    }
  }, [edgePairKey, typedByPair, selectedEdges]);

  // The selected entity's member-asset PORTFOLIO for the inspector ASSETS block —
  // every linked asset (on-canvas junction or hidden leaf alike), identity TOKENS
  // excluded (they're the header `$SYM` chip, never duplicated as a row).
  const selectedPortfolio = useMemo<ChainAssetNode[]>(() => {
    if (!selectedEntity) return [];
    return assets.filter(
      (a) => a.parentSlugs.includes(selectedEntity.slug) && !isIdentityTokenOf(selectedEntity, a),
    );
  }, [selectedEntity, assets]);

  // PATH chain payload (map-v2.html "the connection") — the resolved path turned
  // into hop rows for the inspector: each consecutive node pair → label + narrative
  // when the hop is a typed edge (else "asset of" for satellite hops, or a generic
  // "linked" for a plain association). Resolved-and-found paths only.
  const pathChain = useMemo<{ hops: RailPathHop[]; from: string; to: string } | null>(() => {
    if (!pathResult?.found || pathResult.nodes.length < 2) return null;
    const nodeInfo = (id: string): RailPathHop['from'] => {
      const a = assetById.get(id);
      if (a) return { name: a.name, image: a.image, slug: id, isAsset: true };
      const e = bySlug.get(id);
      return { name: e?.name ?? id, image: e?.image, slug: id, isAsset: false };
    };
    const hops: RailPathHop[] = [];
    for (let i = 0; i < pathResult.nodes.length - 1; i++) {
      const fromId = pathResult.nodes[i];
      const toId = pathResult.nodes[i + 1];
      const from = nodeInfo(fromId);
      const to = nodeInfo(toId);
      const tes = typedByPair.get(pairKey(fromId, toId));
      if (tes && tes.length > 0) {
        // forward label from THIS hop's direction (fromId → toId)
        const labels = [...new Set(tes.map((te) => labelFrom(te, fromId)))];
        hops.push({
          from,
          to,
          label: labels.slice(0, 2).join(' · ') + (labels.length > 2 ? ' …' : ''),
          description: tes.find((te) => te.description)?.description ?? null,
          typed: true,
        });
      } else {
        hops.push({
          from,
          to,
          label: from.isAsset || to.isAsset ? 'asset of' : 'linked',
          description: null,
          typed: false,
        });
      }
    }
    return {
      hops,
      from: nodeInfo(pathResult.nodes[0]).name,
      to: nodeInfo(pathResult.nodes[pathResult.nodes.length - 1]).name,
    };
  }, [pathResult, typedByPair, bySlug, assetById]);

  // Phone bottom sheet: Esc dismisses. Deliberately NO body scroll lock — a
  // dismissable sheet doesn't need to freeze the page, and a leaked
  // `overflow:hidden` is a catastrophic failure mode (the page-wide scroll-trap
  // Lam hit: the lock engaged while the sheet rendered off-viewport, leaving no
  // touch path to release it). Background-scroll containment is done LOCALLY
  // instead: `touch-action: none` on the overlay root stops touches on the
  // backdrop/handle/footer from panning the document, while the sheet body
  // (`overflow-y-auto overscroll-contain`) still pans normally — touch-action
  // resolution stops at the nearest scroll container, and overscroll-contain
  // kills the end-of-list chain to the page. Nothing leaks: the containment
  // unmounts WITH the sheet.
  // EDGE mode wins over node mode in the inspector when an edge is selected.
  const edgeMode = !!edgePairKey && selectedEdges.length > 0;
  // PATH read shows in the inspector when a path is resolved and the user has no
  // active node/edge selection (path picking routes clicks away from selection,
  // so `selected` is normally null here — but if they DID select, that wins).
  const pathInspect = !!pathChain && !edgeMode && !selected;
  const sheetOpen =
    isPhone && (pathInspect || edgeMode || (!!selected && !!(selectedEntity || selectedAsset)));
  useEffect(() => {
    if (!sheetOpen) return;
    const onKey = (e: KeyboardEvent) => {
      // Esc exits edge mode first (back to the node), else clears selection.
      if (e.key === 'Escape') {
        if (edgePairKey) setEdgePairKey(null);
        else setSelected(null);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sheetOpen]);

  // Desktop EDGE mode: Esc returns to the node inspector (the phone sheet has its
  // own Esc above). One global listener, only while edge mode is open.
  useEffect(() => {
    if (!edgeMode || isPhone) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setEdgePairKey(null);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [edgeMode, isPhone]);

  // PATH mode: Esc exits the path finder (clearing the pair + mode). Only while
  // path mode is active and not currently in a node/edge sheet on phones.
  useEffect(() => {
    if (!pathMode) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !edgeMode && !selected) {
        setPathMode(false);
        setPathEnds([]);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [pathMode, edgeMode, selected]);

  // CONTROL-BAR MENU dismissal — Esc closes the open Filter/Lens popover, and a
  // pointerdown anywhere OUTSIDE the open panel/button does too. The popover and
  // its trigger carry a data attribute so this listener can ignore in-panel
  // clicks (and so it never fights the canvas pan handlers — those targets won't
  // match). `capture` so Esc wins before the path/selection Esc handlers above,
  // and we stop the event so a single Esc only closes the menu (not the map).
  useEffect(() => {
    if (!openMenu) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        setOpenMenu(null);
      }
    };
    const onDown = (e: PointerEvent) => {
      const t = e.target as HTMLElement | null;
      if (t && t.closest('[data-eg-menu]')) return; // click inside panel or trigger
      setOpenMenu(null);
    };
    document.addEventListener('keydown', onKey, true);
    document.addEventListener('pointerdown', onDown, true);
    return () => {
      document.removeEventListener('keydown', onKey, true);
      document.removeEventListener('pointerdown', onDown, true);
    };
  }, [openMenu]);

  // Swipe-down-to-dismiss: a pointer drag captured ONLY on the sheet's grab
  // handle / header region (so content scrolling is never hijacked). The sheet
  // follows the finger (downward only); past 30% of its height OR a downward
  // flick (velocity) it dismisses, otherwise it springs back.
  const sheetWrapRef = useRef<HTMLDivElement>(null);
  const [sheetDragY, setSheetDragY] = useState(0);
  const [sheetDragging, setSheetDragging] = useState(false);
  const sheetDragState = useRef<{ startY: number; lastY: number; lastT: number; vy: number } | null>(
    null,
  );
  useEffect(() => {
    // Reset any in-flight drag whenever the sheet opens/closes (fresh sheet
    // never inherits a stale offset).
    setSheetDragY(0);
    setSheetDragging(false);
    sheetDragState.current = null;
  }, [sheetOpen, selected]);
  const endSheetDrag = useCallback(
    (cancelled: boolean) => {
      const s = sheetDragState.current;
      sheetDragState.current = null;
      setSheetDragging(false);
      if (s && !cancelled) {
        const dy = Math.max(0, s.lastY - s.startY);
        const h = sheetWrapRef.current?.offsetHeight ?? 320;
        // Dismiss on distance (≥30% of the sheet) OR a deliberate flick
        // (>0.5 px/ms downward with a little travel behind it).
        if (dy > h * 0.3 || (dy > 24 && s.vy > 0.5)) {
          setSelected(null);
          return; // the sheet unmounts; the open-effect resets dragY
        }
      }
      setSheetDragY(0); // spring back
    },
    [setSelected],
  );
  const sheetDragHandlers: SheetDragHandlers = {
    onPointerDown: (e) => {
      if (!e.isPrimary) return;
      try {
        e.currentTarget.setPointerCapture(e.pointerId);
      } catch {
        /* already-released pointer — drag still tracks via the region's events */
      }
      sheetDragState.current = {
        startY: e.clientY,
        lastY: e.clientY,
        lastT: performance.now(),
        vy: 0,
      };
      setSheetDragging(true);
    },
    onPointerMove: (e) => {
      const s = sheetDragState.current;
      if (!s) return;
      const now = performance.now();
      const dt = now - s.lastT;
      if (dt > 0) s.vy = (e.clientY - s.lastY) / dt;
      s.lastY = e.clientY;
      s.lastT = now;
      setSheetDragY(Math.max(0, e.clientY - s.startY));
    },
    onPointerUp: () => endSheetDrag(false),
    onPointerCancel: () => endSheetDrag(true),
  };

  // THE unified inspector (one instance, two containers): a sibling rail on
  // desktop/tablet, a fixed bottom sheet over the canvas on phones.
  const inspector = (
    <GraphRail
      entity={selectedEntity}
      asset={selectedAsset}
      edges={edgeMode ? selectedEdges : null}
      // PATH read (wins over the idle state; node/edge selection wins over it).
      path={pathInspect ? pathChain : null}
      onClosePath={() => {
        setPathMode(false);
        setPathEnds([]);
      }}
      relationships={selectedRels}
      portfolio={selectedPortfolio}
      // Node/asset/counterpart selection always exits edge mode (selectNode).
      onSelect={(slug) => selectNode(slug)}
      // Promote a relationship row to EDGE mode (edge-as-first-class alt entry).
      onInspectEdge={(otherSlug) => {
        if (selected) setEdgePairKey(pairKey(selected, otherSlug));
      }}
      // EDGE mode × — back to the node inspector (phone sheet × still dismisses).
      onCloseEdge={() => setEdgePairKey(null)}
      inChain={bySlug}
      isMobile={isMobile}
      desktopHeight={H}
      sheet={isPhone}
      onClose={isPhone ? () => setSelected(null) : undefined}
      dragHandleProps={isPhone ? sheetDragHandlers : undefined}
      reducedMotion={reducedMotion}
    />
  );
  // PORTALED to <body>: the graph <section> picks up `contain: layout style`
  // from a global stylesheet rule, which makes it the containing block for
  // `position:fixed` descendants — rendered in place, the "fixed" sheet anchors
  // to the SECTION instead of the viewport. Tap a directory card halfway down
  // the page and the sheet (and its dismissing backdrop) land off-screen above
  // you. The portal escapes any ancestor containment/transform for good.
  // `touch-none` on the overlay root: touches OUTSIDE the sheet's internal
  // scroll container (backdrop, grab handle, header, footer) never pan the
  // document behind the open sheet. The sheet body still scrolls — touch-action
  // resolution stops at the nearest scroll container (the body's
  // overflow-y-auto div), which also carries overscroll-contain so its
  // end-of-scroll never chains to the page. No body-level lock to leak.
  const phoneSheet =
    sheetOpen && mounted
      ? createPortal(
          <div
            className="eg-motion-scope fixed inset-0 z-[100] touch-none"
            role="dialog"
            aria-modal="true"
            aria-label="Entity inspector"
          >
            <button
              type="button"
              aria-label="Close inspector"
              onClick={() => setSelected(null)}
              className="absolute inset-0 cursor-default bg-ink/50 backdrop-blur-[1px]"
              // Inspector-open motion (R7): backdrop fades in with the sheet.
              style={{ animation: 'eg-fade-in 200ms ease-out' }}
            />
            <div
              ref={sheetWrapRef}
              className="absolute inset-x-0 bottom-0"
              style={{
                transform: sheetDragY > 0 ? `translate3d(0, ${sheetDragY}px, 0)` : undefined,
                // Finger-follow while dragging; eased spring-back on release.
                transition: sheetDragging ? 'none' : 'transform 220ms cubic-bezier(0.22, 0.8, 0.26, 0.99)',
                // Inspector-open motion (R7): the sheet slides up ONCE on open
                // (the wrapper persists across entity switches, so no re-slide).
                // Both gated by the eg-motion-scope media rule, hook aside.
                animation: reducedMotion ? undefined : 'eg-sheet-in 300ms cubic-bezier(0.22, 0.8, 0.26, 0.99)',
              }}
            >
              {inspector}
            </div>
          </div>,
          document.body,
        )
      : null;

  // Type-filter chips — rendered as a desktop overlay AND a mobile row below the
  // canvas (shared so both stay in sync).
  //
  // "All" is a FULL RESET, not just the type-filter clear: with several dim axes
  // (type × off-chain × selection neighborhood) compounding silently, a user who
  // stacks two or three reads the graph as STUCK — clicking "All" must always
  // return the whole map (Lam bug report 2026-06-12: "hitting different filters
  // doesn't light up the entities you'd expect but only like 1 or 2").
  // It clears typeFilter + guestsOnly + edge mode + node selection, and its lit
  // state reflects ALL of those axes being off.
  // The dots MUST equal the rings on the canvas (single-sourced from TYPE_RING),
  // else the legend teaches the wrong key. Project / Game / Character are split
  // now that each has a real ring color instead of a shared `--divider` hairline.
  // Chips render only for types actually present, so the panel stays tight.
  const presentTypes = useMemo(() => {
    const s = new Set<string>();
    for (const n of nodes) if (n.kind === 'entity' && n.ent?.type) s.add(n.ent.type);
    return s;
  }, [nodes]);
  const ALL_TYPE_CHIPS: ReadonlyArray<readonly [string, string | null, readonly string[] | null]> = [
    ['All', null, null],
    ['Project', TYPE_RING.project, ['project']],
    ['Game', TYPE_RING.game, ['game']],
    ['Character', TYPE_RING.character, ['character']],
    ['Person', TYPE_RING.person, ['person']],
    ['Org', TYPE_RING.organization, ['organization']],
    ['Protocol', chainColor, ['protocol']],
  ];
  const TYPE_CHIPS = ALL_TYPE_CHIPS.filter(
    ([, , types]) => types === null || types.some((t) => presentTypes.has(t)),
  );
  // RESET VIEW - clears EXACTLY the axes the `Filters · N` badge counts, so the
  // badge is a promise the button keeps (review item 5). The Lens overlays
  // (sentiment / path) keep their own `Lens •` dot and their own exits; the
  // selection is a read, not a filter, and clears on a background click.
  const viewAxes = [
    typeFilter !== null,
    guestsOnly,
    assetMode !== 'shared',
    showInferred,
    focusCore,
  ];
  const activeFilterCount = viewAxes.filter(Boolean).length;
  const resetView = () => {
    setTypeFilter(null);
    setGuestsOnly(false);
    setAssetMode('shared');
    setShowInferred(false);
    try {
      window.localStorage.setItem(INFERRED_PREF_KEY, '0');
    } catch {
      /* storage unavailable - the reset still applies for this session */
    }
    setFocusCore(false);
  };
  // The "All" chip lives in the TYPE group and now resets the TYPE axis only -
  // and its lit state reads only the type axis (review item 5). It used to clear
  // guests / sentiment / path / selection too, so it sat unlit while claiming to
  // be "All", and it silently undid controls from three other groups. The
  // whole-view escape hatch is `Reset view` at the foot of this panel.
  const renderTypeChip = (label: string, c: string | null, types: readonly string[] | null) => {
    const active =
      types === null
        ? typeFilter === null
        : !!typeFilter && typeFilter.join() === [...types].join();
    return (
      <button
        key={label}
        type="button"
        aria-pressed={active}
        onClick={() => (types === null ? setTypeFilter(null) : setTypeFilter(active ? null : [...types]))}
        className={`pointer-events-auto inline-flex min-h-[36px] flex-shrink-0 cursor-pointer items-center gap-1.5 rounded px-2 py-2 transition-colors ${
          active ? 'bg-scout-gold/15' : 'hover:bg-surface2/70'
        }`}
      >
        {c && (
          <span className="h-2.5 w-2.5 rounded-full border bg-surface2" style={{ borderColor: c }} />
        )}
        <Mono size={10} color={active ? 'var(--amber)' : 'var(--slate)'}>
          {label}
        </Mono>
      </button>
    );
  };
  // "Assets" chip — a 3-STATE CYCLE for the satellite layer (not a dim filter):
  // shared junctions (default) → all satellites → off. Hidden assets leave the
  // sim entirely; survivors keep their settled positions (see useForce).
  const cycleAssetMode = () => {
    const next = NEXT_ASSET_MODE[assetMode];
    // Deselect an inspected asset the next state hides (a stranded inspector
    // for a node that no longer exists on the canvas).
    if (selected && assetById.has(selected)) {
      const stillVisible = next === 'all' || (next === 'shared' && junctionIds.has(selected));
      if (!stillVisible) setSelected(null);
    }
    setAssetMode(next);
  };
  const renderAssetsChip = () =>
    assets.length > 0 ? (
      <button
        key="assets"
        type="button"
        aria-pressed={assetMode !== 'none'}
        aria-label={`Asset satellites: ${
          assetMode === 'shared' ? 'shared only' : assetMode === 'all' ? 'all' : 'off'
        } — click to cycle`}
        onClick={cycleAssetMode}
        className={`pointer-events-auto inline-flex min-h-[36px] flex-shrink-0 cursor-pointer items-center gap-1.5 rounded px-2 py-2 transition-colors ${
          assetMode !== 'none' ? 'bg-scout-gold/15' : 'hover:bg-surface2/70'
        }`}
      >
        <span
          className="h-2 w-2 flex-shrink-0 rotate-45 border border-dashed bg-surface2"
          style={{ borderColor: assetMode === 'none' ? 'var(--slate)' : 'var(--slate-light)' }}
        />
        <Mono size={10} color={assetMode !== 'none' ? 'var(--amber)' : 'var(--slate)'}>
          {ASSET_MODE_LABEL[assetMode]}
        </Mono>
      </button>
    ) : null;
  // "Layout" chip — the EVALUATION-PROTOTYPE toggle: Orbit (deterministic, the
  // default being evaluated) ↔ Force (organic sim). Styled/positioned exactly
  // like the Assets chip. Hidden when orbital mode is unavailable (<2 hubs) —
  // there's nothing to evaluate against, and force is forced anyway.
  const renderLayoutChip = () =>
    orbital.available ? (
      <button
        key="layout"
        type="button"
        aria-pressed={layoutMode === 'orbit'}
        aria-label={`Graph layout: ${layoutMode === 'orbit' ? 'orbital' : 'force-directed'} — click to switch`}
        onClick={() => setLayoutMode((m) => NEXT_LAYOUT_MODE[m])}
        className={`pointer-events-auto inline-flex min-h-[36px] flex-shrink-0 cursor-pointer items-center gap-1.5 rounded border-[0.5px] border-divider bg-surface/80 px-2.5 py-2 transition-colors ${
          layoutMode === 'orbit' ? 'bg-scout-gold/15' : 'hover:bg-surface2/70'
        }`}
      >
        <span
          className="h-2.5 w-2.5 flex-shrink-0 rounded-full border bg-surface2"
          style={{ borderColor: layoutMode === 'orbit' ? 'var(--amber)' : 'var(--slate)' }}
        />
        <Mono size={10} color={layoutMode === 'orbit' ? 'var(--amber)' : 'var(--slate)'}>
          {LAYOUT_MODE_LABEL[layoutMode]}
        </Mono>
      </button>
    ) : null;

  // FOCUS chip — the ego core as a LENS. Off by default: the map opens on the
  // WHOLE ecosystem (Lam 2026-09-04). On, it lights the connected spine and dims
  // the rest to the 0.45 floor. Hidden when focusing wouldn't narrow anything.
  const focusAvailable = focusCount > 0 && focusCount < memberNodeCount;
  const renderSpineChip = () =>
    focusAvailable ? (
      <button
        key="focus"
        type="button"
        aria-pressed={focusCore}
        aria-label={
          focusCore
            ? `Focus is on — ${focusCount} connected entities lit, the rest dimmed. Click to light the whole map.`
            : `Focus the ${focusCount} most-connected entities (nothing is hidden — the rest dim)`
        }
        onClick={() => setFocusCore((v) => !v)}
        className={`pointer-events-auto inline-flex min-h-[36px] flex-shrink-0 cursor-pointer items-center gap-1.5 rounded border-[0.5px] px-2.5 py-2 transition-colors ${
          focusCore
            ? 'border-scout-gold/40 bg-scout-gold/15'
            : 'border-divider bg-surface/80 hover:bg-surface2/70'
        }`}
      >
        <Mono size={10} color={focusCore ? 'var(--amber)' : 'var(--parchment-dim)'}>
          {focusCore ? `Focus · ${focusCount}` : 'Focus'}
        </Mono>
      </button>
    ) : null;
  const renderMoreChip = () => null;

  // "Cross-chain" filter chip — toggles guestsOnly, dimming every on-chain entity
  // so only cross-chain entities (on another chain, joined by a verified edge)
  // stay lit. Styled identically to its sibling filter chips (it sits in a
  // clickable row, so a static legend read as broken). Only shown when one is
  // actually on the canvas. The dashed dot mirrors the node's "lives elsewhere"
  // ring — a feature cue ("connections across chains"), not an exclusion.
  const hasGuests = nodes.some((n) => n.kind === 'entity' && !!n.ent?.offChain);
  const renderOffChainChip = () =>
    hasGuests ? (
      <button
        key="off-chain"
        type="button"
        aria-pressed={guestsOnly}
        aria-label="Filter to cross-chain entities"
        onClick={() => setGuestsOnly((v) => !v)}
        className={`pointer-events-auto inline-flex min-h-[36px] flex-shrink-0 cursor-pointer items-center gap-1.5 rounded px-2 py-2 transition-colors ${
          guestsOnly ? 'bg-scout-gold/15' : 'hover:bg-surface2/70'
        }`}
        title="Cross-chain — an entity on another chain, pulled in by a verified relationship"
      >
        <span
          className="h-2.5 w-2.5 flex-shrink-0 rounded-full border border-dashed bg-surface2"
          style={{ borderColor: 'var(--slate-light)', opacity: 0.8 }}
        />
        <Mono size={10} color={guestsOnly ? 'var(--amber)' : 'var(--slate)'}>
          Cross-chain
        </Mono>
      </button>
    ) : null;

  // PATH FINDER toggle — enter/exit path-picking. Entering resets any half-picked
  // pair; exiting clears the pair entirely (folded through the same setters so the
  // dim path releases). Selection is left intact (a path is a separate read).
  const togglePathMode = () => {
    setPathMode((on) => !on);
    setPathEnds([]);
  };

  // "Lens" menu overlays (map-v2.html) — after the relationship-type lenses were
  // removed (buggy — Lam 2026-06-24), the menu carries just the SENTIMENT color
  // overlay + the PATH finder, each a labeled captioned row in the Lens popover.
  const hasPath = links.length > 0;
  const renderSentimentButton = () => (
    <button
      type="button"
      aria-pressed={sentimentOn}
      aria-label="Sentiment color overlay — tint node rings by social sentiment"
      onClick={() => setSentimentOn((v) => !v)}
      className={`pointer-events-auto inline-flex min-h-[36px] flex-shrink-0 cursor-pointer items-center gap-1 rounded px-2 py-2 transition-colors ${
        sentimentOn ? 'bg-scout-gold/15' : 'hover:bg-surface2/70'
      }`}
    >
      <Mono size={10} color={sentimentOn ? 'var(--amber)' : 'var(--slate)'}>
        Sentiment
      </Mono>
    </button>
  );
  const renderPathButton = () => (
    <button
      type="button"
      aria-pressed={pathMode}
      aria-label="Path finder — pick two entities to see how they connect"
      onClick={togglePathMode}
      className={`pointer-events-auto inline-flex min-h-[36px] flex-shrink-0 cursor-pointer items-center gap-1 rounded px-2 py-2 transition-colors ${
        pathMode ? 'bg-scout-gold/15' : 'hover:bg-surface2/70'
      }`}
    >
      <Mono size={10} color={pathMode ? 'var(--amber)' : 'var(--slate)'}>
        Path
      </Mono>
    </button>
  );

  // ── popover plumbing (PO declutter 2026-06-12) ──────────────────────────────
  // Which child groups exist drives whether each dropdown shows at all.
  // Type-lenses ripped out (see lensPanel) — the Lens menu now gates on the overlays only.
  const lensAvailable = hasRecon || hasPath;
  // ACTIVE-STATE INDICATORS — a COLLAPSED dropdown must advertise that it's
  // holding a selection, else the PO sees a dimmed map with no visible cause.
  // Lens menu "on" dot — shown when the Sentiment overlay or the Path finder is active.
  const lensOverlayOn = sentimentOn || pathMode;

  // The shared dropdown TRIGGER — a chip-styled button matching the legend chips.
  // It opens upward-anchored popovers (the bar sits at the canvas bottom), reads
  // "on" when it holds any active child, and carries data-eg-menu so the global
  // dismiss listener ignores its own clicks.
  const renderMenuTrigger = (
    menu: 'filter' | 'lens',
    label: React.ReactNode,
    isActive: boolean,
  ) => {
    const open = openMenu === menu;
    return (
      <button
        type="button"
        data-eg-menu
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpenMenu((m) => (m === menu ? null : menu))}
        className={`pointer-events-auto inline-flex min-h-[36px] flex-shrink-0 cursor-pointer items-center gap-1.5 rounded border-[0.5px] px-2.5 py-2 transition-colors ${
          open || isActive ? 'border-scout-gold/40 bg-scout-gold/15' : 'border-divider bg-surface/80 hover:bg-surface2/70'
        }`}
      >
        <Mono size={12} color={open || isActive ? 'var(--amber)' : 'var(--parchment-dim)'}>
          {label}
        </Mono>
        <span aria-hidden style={{ fontSize: 11, lineHeight: 1, color: open || isActive ? 'var(--amber)' : 'var(--slate)' }}>
          ▾
        </span>
      </button>
    );
  };

  // A captioned row inside the Lens popover: the control button + a dim one-liner.
  // whitespace-nowrap + the panel's wider maxWidth keep each caption on ONE line
  // (the PO saw captions clipping at the old 240px width).
  const lensRow = (button: React.ReactNode, caption: string) => (
    <div className="flex items-center gap-2.5">
      {button}
      <Mono size={11} color="var(--slate)" style={{ opacity: 0.9, whiteSpace: 'nowrap' }}>
        {caption}
      </Mono>
    </div>
  );

  // FILTER popover body — the "what's shown" controls, grouped under thin labels.
  const filterPanel = (
    <div role="group" aria-label="Filters" className="flex flex-col gap-2.5" style={{ width: 250 }}>
      <Mono size={10} color="var(--slate)" style={{ letterSpacing: '0.1em', textTransform: 'uppercase' }}>
        Type
      </Mono>
      <div className="flex flex-wrap items-center gap-1">
        {TYPE_CHIPS.map(([label, c, types]) => renderTypeChip(label, c, types))}
      </div>
      {/* "Show all entities" (renderSpineChip) was pulled OUT to a standalone chip
          beside the Layout control — it was too buried in here (Lam 2026-06-24). */}
      {/* EVIDENCE — the typed-only doctrine's one escape hatch. Inferred links are
          `associatedTags` co-occurrence: a hint that two entities turn up together,
          NOT a verified relationship. They render as thin grey dashes so they can
          never be mistaken for the gold typed spine. */}
      <div className="h-px bg-divider" />
      <Mono size={10} color="var(--slate)" style={{ letterSpacing: '0.1em', textTransform: 'uppercase' }}>
        Evidence
      </Mono>
      <div className="flex flex-col gap-1">
        <button
          type="button"
          aria-pressed={showInferred}
          onClick={toggleInferred}
          className={`pointer-events-auto inline-flex min-h-[36px] flex-shrink-0 cursor-pointer items-center gap-1.5 self-start rounded px-2 py-2 transition-colors ${
            showInferred ? 'bg-scout-gold/15' : 'hover:bg-surface2/70'
          }`}
        >
          <svg width="14" height="4" aria-hidden className="flex-shrink-0">
            <line
              x1="0"
              y1="2"
              x2="14"
              y2="2"
              stroke={INFERRED_EDGE_COLOR}
              strokeWidth="1.2"
              strokeDasharray={INFERRED_EDGE_DASH}
            />
          </svg>
          <Mono size={10} color={showInferred ? 'var(--amber)' : 'var(--slate)'}>
            Show inferred links
          </Mono>
        </button>
        <Mono size={10} color="var(--slate)" style={{ opacity: 0.85, lineHeight: 1.5 }}>
          {inferredCount > 0
            ? `${inferredCount} untyped co-occurrence${inferredCount === 1 ? '' : 's'} — a hint, not a verified relationship.`
            : 'Untyped co-occurrences — a hint, not a verified relationship.'}
        </Mono>
      </div>
      {(assets.length > 0 || hasGuests) && (
        <>
          <div className="h-px bg-divider" />
          <Mono size={10} color="var(--slate)" style={{ letterSpacing: '0.1em', textTransform: 'uppercase' }}>
            Flags
          </Mono>
          <div className="flex flex-wrap items-center gap-1">
            {renderAssetsChip()}
            {renderOffChainChip()}
          </div>
        </>
      )}
      {/* RESET VIEW - clears exactly the axes the `Filters · N` badge counts. */}
      <div className="h-px bg-divider" />
      <button
        type="button"
        onClick={resetView}
        disabled={activeFilterCount === 0}
        className={`inline-flex min-h-[36px] items-center gap-1.5 self-start rounded px-2 py-2 transition-colors ${
          activeFilterCount === 0
            ? 'cursor-default opacity-40'
            : 'cursor-pointer hover:bg-surface2/70'
        }`}
      >
        <Mono size={10} color={activeFilterCount === 0 ? 'var(--slate)' : 'var(--amber)'}>
          {activeFilterCount === 0
            ? 'Reset view'
            : `Reset view · ${activeFilterCount} active`}
        </Mono>
      </button>
    </div>
  );

  // LENS popover body. The relationship-type lenses (Founders / Structure / Linked)
  // were fully REMOVED 2026-06-24 (buggy — Lam), along with their state + per-edge
  // dim machinery; the menu now carries only the Sentiment overlay + Path finder.
  const lensPanel = (
    <div role="group" aria-label="Lenses" className="flex flex-col gap-2.5" style={{ maxWidth: 360 }}>
      {hasRecon && lensRow(renderSentimentButton(), 'Tint rings by social sentiment')}
      {hasPath && lensRow(renderPathButton(), 'Trace how two entities connect')}
    </div>
  );

  // The popover PANEL chrome — a surface card anchored to its trigger. `dir`
  // controls the open direction: 'up' (default) for the mobile row's triggers at
  // the bottom (clears the canvas edge / mobile nav); 'down' for the desktop
  // top-left control cluster (the consolidated Filters/Lens menus, Lam 2026-06-24).
  // pointer-events-auto (captures its own clicks) inside the otherwise
  // pointer-events-none overlay; data-eg-menu keeps the dismiss listener off it.
  const renderPopover = (
    menu: 'filter' | 'lens',
    body: React.ReactNode,
    dir: 'up' | 'down' = 'up',
  ) =>
    openMenu === menu ? (
      <div
        data-eg-menu
        role="menu"
        onPointerDown={(e) => e.stopPropagation()}
        style={dir === 'down' ? { transformOrigin: 'top left' } : undefined}
        className={`eg-menu-pop pointer-events-auto absolute left-0 z-10 rounded border-[0.5px] border-divider bg-surface p-3.5 shadow-lg shadow-black/30 ${
          dir === 'down' ? 'top-full mt-2' : 'bottom-full mb-2'
        }`}
      >
        {body}
      </div>
    ) : null;

  // Path-finder microcopy: a single guiding line shown while path mode is active.
  const pathMicrocopy = !pathMode
    ? null
    : pathEnds.length === 0
      ? 'Path finder · click the first entity'
      : pathEnds.length === 1
        ? 'Path finder · now click the second entity'
        : pathResult?.found
          ? `Path · ${pathResult.nodes.length} ${pathResult.nodes.length === 1 ? 'node' : 'nodes'} · ${pathResult.edgeKeys.size} ${pathResult.edgeKeys.size === 1 ? 'hop' : 'hops'}`
          : 'No connection on the record between these two';

  // Empty graph (entities exist but none share an in-chain association yet).
  // The unified inspector still serves directory-card selections here.
  if (nodes.length === 0) {
    return (
      <section aria-label="Relationship map" className="eg-motion-scope mb-10">
        <GraphMotionStyles />
        <GraphHeader
          chainLabel={chainLabel}
          entityCount={0}
          unconnectedCount={0}
          typedCount={0}
          typedTotal={0}
          inferredCount={0}
          inferredShown={showInferred}
        />
        <div className="rounded border-[0.5px] border-divider bg-surface p-6">
          <p className="font-display text-sm text-parchment-dim">
            The relationship map fills in as connections between {chainLabel} entities are
            verified. Browse the full directory below in the meantime.
          </p>
        </div>
        {!isPhone && (selectedEntity || selectedAsset) && <div className="mt-3">{inspector}</div>}
        {phoneSheet}
      </section>
    );
  }

  return (
    <section aria-label="Relationship map" className="eg-motion-scope mb-10">
      <GraphMotionStyles />
      <GraphHeader
        chainLabel={chainLabel}
        // MEMBER nodes only — off-chain guests never inflate the coverage strip.
        entityCount={memberNodeCount}
        unconnectedCount={unconnectedCount}
        typedCount={typedRelCount}
        typedTotal={typedRelTotal}
        inferredCount={inferredCount}
        inferredShown={showInferred}
      />

      <div className="grid gap-3 lg:grid-cols-[1fr_320px]">
        {/* ── graph canvas ──────────────────────────────────────────────── */}
        {/* GRAPH COLUMN - the canvas AND its arrow legend are ONE grid cell.
            (They were two, which handed the legend the inspector's column and
            pushed the inspector down a row.) min-w-0 lets the canvas shrink. */}
        <div className="flex min-w-0 flex-col">
        <div
          ref={wrap}
          className="relative overflow-hidden rounded border-[0.5px] border-divider bg-ink"
          style={wrapHeightStyle}
        >
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0"
            style={{
              background:
                'radial-gradient(ellipse 60% 70% at 50% 50%, rgba(232,168,50,0.04), transparent 70%)',
            }}
          />
          <svg
            ref={svgRef}
            width="100%"
            height={H}
            viewBox={`0 0 ${W} ${H}`}
            className="relative block touch-none"
            style={{ cursor: 'grab' }}
          >
            {/* ARROWHEAD (2026-09-04): ONE marker, always at the line's END. A
                link whose ontology direction runs against the drawn source→target
                order is DRAWN tail-first instead (see `arrow: 'backward'`), which
                avoids depending on `orient="auto-start-reverse"`. userSpaceOnUse
                keeps the head a constant size regardless of stroke width; the
                line's own `opacity` (confidence) applies to the marker too, so a
                low-confidence claim gets a faint arrow. */}
            <defs>
              <marker
                id="eg-arrow"
                viewBox="0 0 10 10"
                refX="9"
                refY="5"
                markerUnits="userSpaceOnUse"
                markerWidth="9"
                markerHeight="9"
                orient="auto"
              >
                <path d="M0,1 L9,5 L0,9 Z" fill={TYPED_EDGE_COLOR} />
              </marker>
            </defs>
            {/* background catcher: pan + click-to-deselect (behind the transformed graph) */}
            <rect
              x={0}
              y={0}
              width={W}
              height={H}
              fill="transparent"
              onPointerDown={onBgDown}
              onClick={onBgClick}
            />
            <g transform={`translate(${view.x},${view.y}) scale(${view.k})`}>
              {/* THE "NOT YET CONNECTED" RING. Drawn first, so it sits behind
                  everything. Entities with no typed edge live ON this ellipse,
                  grouped into type sectors. Stroking the band is what turns a
                  scatter of lonely avatars into a legible holding pattern: these
                  are not clutter, they are the backlog the graph is working
                  through, and seeing it shrink IS the growth (Lam 2026-09-04). */}
              {effectiveLayout === 'orbit' && orbital.unconnectedRing && (
                <g aria-hidden style={{ pointerEvents: 'none' }}>
                  <ellipse
                    cx={orbital.unconnectedRing.cx}
                    cy={orbital.unconnectedRing.cy}
                    rx={orbital.unconnectedRing.rx}
                    ry={orbital.unconnectedRing.ry}
                    fill="none"
                    stroke="var(--slate)"
                    strokeWidth={0.75}
                    strokeDasharray="2 9"
                    opacity={0.5}
                  />
                  {/* The band's caption needs room ABOVE the ring; on a phone
                      canvas it lands on the pan/zoom hint, and the header already
                      states the number — so it is a wide-canvas affordance only. */}
                  {W >= 560 && (
                  <text
                    x={orbital.unconnectedRing.cx}
                    y={orbital.unconnectedRing.cy - orbital.unconnectedRing.ry - 12}
                    textAnchor="middle"
                    style={{
                      fontFamily: 'var(--font-mono)',
                      fontSize: 9,
                      letterSpacing: '0.14em',
                      textTransform: 'uppercase',
                      fill: 'var(--slate)',
                      stroke: 'var(--ink)',
                      strokeWidth: 3,
                      paintOrder: 'stroke',
                    }}
                  >
                    Not yet connected · {unconnectedCount}
                  </text>
                  )}
                </g>
              )}
              {/* edges — typed pairs draw a notch stronger than generic fallbacks;
                  a focused node's typed edges get their perspective label at the
                  midpoint (focus-only — permanent labels are noise at 100+). */}
              {links.map((l, i) => {
                const a = pos[l.source];
                const b = pos[l.target];
                if (!a || !b) return null;
                const active =
                  !!neigh &&
                  neigh.has(l.source) &&
                  neigh.has(l.target) &&
                  (l.source === focus || l.target === focus);
                // Type-dim: asset edges follow their ENTITY endpoint only (the
                // other end is an asset and has no entity type). The same predicate
                // also carries the guests-only filter (off-chain entity endpoints
                // stay lit; on-chain endpoints and any asset end dim).
                let typeDim: boolean;
                if (l.asset) {
                  const entEnd = bySlug.get(l.source) ?? bySlug.get(l.target);
                  typeDim = isEntityDimmed(entEnd) || guestsOnly;
                } else {
                  typeDim = isEntityDimmed(bySlug.get(l.source)) || isEntityDimmed(bySlug.get(l.target));
                }
                // PATH dim: when a path is resolved, EVERY edge not a path hop dims;
                // a path hop stays fully lit (regardless of neighbour focus).
                const isPathEdge = !!pathLit && pathResult?.found
                  ? pathResult.edgeKeys.has(pairKey(l.source, l.target))
                  : false;
                const pathDim = !!pathLit && !isPathEdge;
                const dim = (!!neigh && !active && !isPathEdge) || typeDim || pathDim;
                /* Focus-only labels. STORED-DIRECTION, ALWAYS (review blocker 1).
                   This used to read `labelFrom(te, focus)` - the label from the
                   FOCUSED node's perspective - while the arrowhead pointed at the
                   ontology's `to` end. Focus the `to` node of a FOUNDED edge and
                   the canvas said "founded by" with the arrow pointing AT the
                   subject: the line contradicted itself. The midpoint label has
                   no named subject, so it can only be read along the arrow -
                   tail -> label -> head - which means it must be `forwardLabel`.
                   The perspective label survives where the subject IS named: the
                   inspector's relationship rows. */
                let edgeLabel: string | null = null;
                if (active && l.asset) {
                  edgeLabel = 'asset of';
                } else if (active && l.typed && focus) {
                  const tes = typedByPair.get(pairKey(l.source, l.target));
                  if (tes && tes.length > 0) {
                    const labels = [...new Set(tes.map((te) => te.forwardLabel))];
                    edgeLabel =
                      labels.slice(0, 2).join(' · ') + (labels.length > 2 ? ' …' : '');
                  }
                }
                // EDGE AS FIRST-CLASS: typed pairs get a fat, invisible hit line so
                // the edge itself is clickable → inspector EDGE mode. Asset/generic
                // edges aren't promotable (no narrative/provenance to show).
                const edgeKey = l.typed && !l.asset ? pairKey(l.source, l.target) : null;
                /* ── ONTOLOGY-DRIVEN STROKE (2026-09-04) ────────────────────
                   Before this, every non-asset edge was the same amber and typed
                   vs untyped differed only by alpha (0.55 vs 0.30) — invisible at
                   fit zoom, so 63% of the AVAX map read as verified fact. Now:
                     corporate spine  solid gold  + arrowhead in the ontology's
                                                   stored direction
                     symmetric        solid gold, NO arrow (a direction would lie)
                     BUILT_ON         dashed gold + arrowhead
                     inferred         thin grey dash (and off by default)
                   Alpha is the CONFIDENCE (high .9 / medium .6 / low .35). */
                const st = edgeKey ? edgeStyles.get(edgeKey) ?? null : null;
                /* GEOMETRY comes from the pure `edgeGeometry` in graph-style.ts:
                   the head ALWAYS lands within (radius + gap) of the ontology's
                   `to` node, whichever way the drawn pair happens to be stored.
                   That invariant is asserted for all nine types from coordinates
                   (not from the enum) in graph-style.test.ts, so swapping the
                   tail/head branch fails the suite instead of shipping. Path hops
                   and asset satellites never carry a head. */
                const line = edgeGeometry(
                  isPathEdge || l.asset ? null : st,
                  a,
                  b,
                  radiusById.get(l.source) ?? 12,
                  radiusById.get(l.target) ?? 12,
                );
                const geom = (() => {
                  let stroke: string;
                  let width: number;
                  let dash: string | undefined;
                  let opacity: number;
                  if (isPathEdge) {
                    // a path hop reads as the bright connective tissue
                    stroke = 'var(--amber)';
                    width = 2.2;
                    dash = undefined;
                    opacity = 1;
                  } else if (l.asset) {
                    stroke = active ? 'rgba(170,182,196,0.55)' : 'rgba(148,163,184,0.22)';
                    width = active ? 1.2 : 0.9;
                    dash = '2 3';
                    opacity = 1;
                  } else if (st) {
                    stroke = TYPED_EDGE_COLOR;
                    // ORBIT: edges ARE the structure (no spatial proximity encodes
                    // them) and the layout zooms further out - a touch more weight.
                    width = st.width * (active ? 1.35 : 1) * (effectiveLayout === 'orbit' ? 1.1 : 1);
                    dash = st.dash ?? undefined;
                    opacity = active ? Math.min(1, st.alpha + 0.15) : st.alpha;
                  } else {
                    // INFERRED (untyped `related`) - deliberately quiet and grey.
                    stroke = INFERRED_EDGE_COLOR;
                    width = INFERRED_EDGE_WIDTH * (active ? 1.5 : 1);
                    dash = INFERRED_EDGE_DASH;
                    opacity = active ? 0.8 : INFERRED_EDGE_ALPHA;
                  }
                  return { stroke, width, dash, opacity };
                })();
                return (
                  <g key={i} style={{ pointerEvents: 'none' }}>
                    {edgeKey && !dim && (
                      <line
                        x1={a.x}
                        y1={a.y}
                        x2={b.x}
                        y2={b.y}
                        stroke="transparent"
                        // 8px (was 14): with ~110 lines the fat invisible web ate
                        // background clicks — stray clicks selected an edge, dimming
                        // the map to 2 endpoints and reading as "filters stuck".
                        strokeWidth={8}
                        style={{ pointerEvents: 'stroke', cursor: 'pointer' }}
                        onPointerDown={(e) => e.stopPropagation()}
                        onClick={(e) => {
                          e.stopPropagation();
                          setEdgePairKey(edgeKey);
                        }}
                      />
                    )}
                    <line
                      x1={line.x1}
                      y1={line.y1}
                      x2={line.x2}
                      y2={line.y2}
                      stroke={geom.stroke}
                      strokeWidth={geom.width}
                      strokeDasharray={geom.dash}
                      markerEnd={line.arrowed ? 'url(#eg-arrow)' : undefined}
                      opacity={dim ? 0.18 : geom.opacity}
                    />
                    {edgeLabel && (
                      <text
                        x={(a.x + b.x) / 2}
                        y={(a.y + b.y) / 2 - 5}
                        textAnchor="middle"
                        style={{
                          fontFamily: 'var(--font-mono)',
                          fontSize: 9,
                          letterSpacing: '0.06em',
                          textTransform: 'uppercase',
                          fill: l.asset ? 'var(--slate-light)' : 'var(--amber)',
                          // Ink halo so the label reads over crossing edges/nodes.
                          stroke: 'var(--ink)',
                          strokeWidth: 3,
                          paintOrder: 'stroke',
                        }}
                      >
                        {edgeLabel}
                      </text>
                    )}
                  </g>
                );
              })}
              {/* nodes — render the focused node LAST so it (and its halo/label) sits
                  on top of its neighbours instead of being occluded by them. */}
              {(focus ? [...nodes].sort((a, b) => (a.id === focus ? 1 : 0) - (b.id === focus ? 1 : 0)) : nodes).map((n) => {
                const p = pos[n.id];
                if (!p) return null;
                // A picked path endpoint reads as selected (amber ring), so the
                // user sees their choice while picking the other end.
                const isSel = selected === n.id || (pathMode && pathEnds.includes(n.id));
                const isHov = hover === n.id;

                /* ── ASSET satellite — small dashed diamond, neutral/dim ────── */
                if (n.kind === 'asset') {
                  const a = n.asset!;
                  const dim = (!!neigh && !neigh.has(n.id)) || isAssetDimmed(a);
                  const s = n.r;
                  const imgR = s * 0.62; // inscribed-circle logo inside the diamond
                  const clipId = `ganode-${n.id.replace(/[^a-zA-Z0-9_-]/g, '_')}`;
                  const showLabel = isMobile
                    ? isSel || isHov
                    : isSel || isHov || (!!neigh && neigh.has(n.id));
                  return (
                    <g
                      key={n.id}
                      transform={`translate(${p.x},${p.y})`}
                      // DIM FLOOR 0.45 (was 0.22): at 0.22 a selection read as
                      // "the map broke", not "focus" (audit 2026-09-04 §6).
                      style={{ cursor: 'pointer', opacity: dim ? 0.45 : 1, transition: 'opacity 160ms' }}
                      onPointerDown={(e) => onNodeDown(n.id, e)}
                      onClick={() => onNodeClickRouted(n.id, 'asset')}
                      onMouseEnter={() => setHover(n.id)}
                      onMouseLeave={() => setHover(null)}
                    >
                      {/* hover/select halo — always rendered so it fades (R7);
                          pointer-events off (no hit-target growth). */}
                      <rect
                        x={-s - 5}
                        y={-s - 5}
                        width={(s + 5) * 2}
                        height={(s + 5) * 2}
                        transform="rotate(45)"
                        rx={2}
                        fill="none"
                        stroke="var(--amber)"
                        strokeWidth={0.5}
                        style={{
                          opacity: isSel ? 0.6 : isHov ? 0.25 : 0,
                          transition: 'opacity 180ms ease',
                          pointerEvents: 'none',
                        }}
                      />
                      <rect
                        x={-s}
                        y={-s}
                        width={s * 2}
                        height={s * 2}
                        transform="rotate(45)"
                        rx={2}
                        fill={isSel ? 'var(--amber-dim)' : 'var(--surface-2)'}
                        stroke={isSel ? 'var(--amber)' : 'var(--slate-light)'}
                        strokeWidth={isSel ? 1.3 : 0.9}
                        strokeDasharray="3 2"
                        opacity={0.95}
                      />
                      {a.image ? (
                        <>
                          <clipPath id={clipId}>
                            <circle r={imgR} />
                          </clipPath>
                          <image
                            href={a.image}
                            x={-imgR}
                            y={-imgR}
                            width={imgR * 2}
                            height={imgR * 2}
                            preserveAspectRatio="xMidYMid slice"
                            clipPath={`url(#${clipId})`}
                            style={{ pointerEvents: 'none' }}
                          />
                        </>
                      ) : (
                        <text
                          textAnchor="middle"
                          dy={3}
                          style={{
                            fontFamily: 'var(--font-mono)',
                            fontSize: 8,
                            fill: isSel ? 'var(--amber)' : 'var(--slate-light)',
                            pointerEvents: 'none',
                          }}
                        >
                          {a.assetType === 'token' ? '$' : '◆'}
                        </text>
                      )}
                      {showLabel && (
                        <text
                          textAnchor="middle"
                          y={s + 14}
                          style={{
                            fontFamily: 'var(--font-sans)',
                            fontWeight: 500,
                            fontSize: 9.5,
                            fill: isSel ? 'var(--parchment-dim)' : 'var(--slate-light)',
                            stroke: 'var(--ink)',
                            strokeWidth: 3,
                            paintOrder: 'stroke',
                            pointerEvents: 'none',
                          }}
                        >
                          {a.name.length > 15 ? a.name.slice(0, 14) + '…' : a.name}
                        </text>
                      )}
                    </g>
                  );
                }

                /* ── ENTITY node ────────────────────────────────────────────── */
                const ent = n.ent!;
                // GUEST: the dashed type ring + home-chain badge mark "lives on
                // another chain" — rendered at FULL opacity (de-ghosted); an
                // off-chain entity is a real place, not a faded visitor.
                const isGuest = !!ent.offChain;
                const homeBadge = isGuest ? guestBadge(ent.offChain) : null;
                const dim = (!!neigh && !neigh.has(n.id)) || isEntityDimmed(ent);
                // SENTIMENT overlay tints the ring (selection still wins → amber).
                const ring = isSel ? 'var(--amber)' : sentimentRing(ent) ?? typeStroke(ent.type);
                const ringDash = isGuest ? '3 3' : undefined;
                const fill = isSel ? 'var(--amber-dim)' : 'var(--surface-2)';
                const img = ent.image;
                const clipId = `gnode-${n.id}`;
                // RESTING LABELS now apply on MOBILE too - the budget is scaled
                // to the canvas width, so a phone gets a legible subset instead
                // of an anonymous smear of avatars (review item 8).
                const showLabel =
                  isSel || isHov || (!!neigh && neigh.has(n.id)) || restingLabels.has(n.id);
                return (
                  <g
                    key={n.id}
                    transform={`translate(${p.x},${p.y})`}
                    style={{
                      cursor: 'pointer',
                      // DIM FLOOR 0.45 (was 0.3) — see the asset node above.
                      opacity: dim ? 0.45 : 1,
                      transition: 'opacity 160ms',
                    }}
                    onPointerDown={(e) => onNodeDown(n.id, e)}
                    onClick={() => onNodeClickRouted(n.id, 'entity')}
                    onMouseEnter={() => setHover(n.id)}
                    onMouseLeave={() => setHover(null)}
                  >
                    {/* hover/select halo — ALWAYS rendered so it fades in/out
                        (180ms) instead of popping (R7); pointer-events off so
                        the invisible ring never widens the node's hit target. */}
                    <circle
                      r={n.r + 7}
                      fill="none"
                      stroke={ring}
                      strokeWidth="0.5"
                      style={{
                        opacity: isSel ? 0.6 : isHov ? 0.25 : 0,
                        transition: 'opacity 180ms ease',
                        pointerEvents: 'none',
                      }}
                    />
                    {/* one-shot select PING (R7): an expanding ring fired when
                        this node becomes the selection (conditional mount =
                        replay per node). JS-gated under reduced motion — with
                        the keyframes killed it would otherwise sit there as a
                        permanent extra ring. */}
                    {isSel && !reducedMotion && (
                      <circle
                        r={n.r + 2}
                        fill="none"
                        stroke="var(--amber)"
                        strokeWidth={1.2}
                        style={{
                          transformBox: 'fill-box',
                          transformOrigin: 'center',
                          animation: 'eg-select-ping 520ms ease-out forwards',
                          pointerEvents: 'none',
                        }}
                      />
                    )}
                    {isSel && <circle r={n.r} fill="none" stroke="var(--amber)" strokeWidth="6" opacity="0.12" />}
                    {img ? (
                      // Image-avatar node: circular-clipped logo + a thin type-colored ring.
                      <>
                        <clipPath id={clipId}>
                          <circle r={n.r} />
                        </clipPath>
                        <circle r={n.r} fill="var(--surface-2)" />
                        <image
                          href={img}
                          x={-n.r}
                          y={-n.r}
                          width={n.r * 2}
                          height={n.r * 2}
                          preserveAspectRatio="xMidYMid slice"
                          clipPath={`url(#${clipId})`}
                          style={{ pointerEvents: 'none' }}
                        />
                        <circle
                          r={n.r}
                          fill={isSel ? 'rgba(232,168,50,0.16)' : 'none'}
                          stroke={ring}
                          strokeWidth={isSel ? 2 : 1.25}
                          strokeDasharray={ringDash}
                        />
                      </>
                    ) : (
                      // Fallback: initials tile (entities without a logo).
                      <>
                        <circle
                          r={n.r}
                          fill={fill}
                          stroke={ring}
                          strokeWidth={isSel ? 1.6 : 1}
                          strokeDasharray={ringDash}
                        />
                        <text
                          textAnchor="middle"
                          dy={n.r > 20 ? 5 : 4}
                          style={{
                            fontFamily: 'var(--font-sans)',
                            fontWeight: 700,
                            fontSize: Math.min(n.r * 0.78, 13),
                            fill: isSel ? 'var(--amber)' : 'var(--parchment-dim)',
                            pointerEvents: 'none',
                            letterSpacing: '-0.02em',
                          }}
                        >
                          {initials(ent.name)}
                        </text>
                      </>
                    )}
                    {/* live "tracked" dot (we actively poll this entity's handle) */}
                    {ent.tracked && (
                      <circle
                        cx={n.r * 0.7}
                        cy={-n.r * 0.7}
                        r={3.2}
                        fill="var(--green-text)"
                        stroke="var(--ink)"
                        strokeWidth={1.2}
                      />
                    )}
                    {/* ◆n — hidden-asset count (junction rule): depth stays visible
                        without drawing the leaf fan; the portfolio itself lives in
                        the inspector's ASSETS block. Deliberately tiny + slate so it
                        never reads as a second label (top-left, clear of the tracked
                        dot top-right and the name label below). */}
                    {(hiddenByEntity.get(n.id) ?? 0) > 0 && (
                      <text
                        textAnchor="end"
                        x={-n.r * 0.62}
                        y={-n.r * 0.62}
                        style={{
                          fontFamily: 'var(--font-mono)',
                          fontSize: 8,
                          fill: 'var(--slate-light)',
                          stroke: 'var(--ink)',
                          strokeWidth: 2.5,
                          paintOrder: 'stroke',
                          pointerEvents: 'none',
                        }}
                      >
                        ◆{hiddenByEntity.get(n.id)}
                      </text>
                    )}
                    {/* GUEST home-chain micro-badge — ◆n style (mono 8, slate,
                        ink halo), bottom-right (top-left is ◆n's slot, top-right
                        is the tracked dot, the name label sits centered below). */}
                    {homeBadge && (
                      <text
                        textAnchor="start"
                        x={n.r * 0.62}
                        y={n.r * 0.62 + 7}
                        style={{
                          fontFamily: 'var(--font-mono)',
                          fontSize: 8,
                          fill: 'var(--slate-light)',
                          stroke: 'var(--ink)',
                          strokeWidth: 2.5,
                          paintOrder: 'stroke',
                          pointerEvents: 'none',
                        }}
                      >
                        {homeBadge}
                      </text>
                    )}
                    {showLabel && (
                      <text
                        textAnchor="middle"
                        y={n.r + 15}
                        style={{
                          fontFamily: 'var(--font-sans)',
                          fontWeight: 600,
                          fontSize: 11,
                          fill: isSel ? 'var(--parchment)' : 'var(--parchment-dim)',
                          // Ink halo so labels stay legible over neighbouring image nodes.
                          stroke: 'var(--ink)',
                          strokeWidth: 3,
                          paintOrder: 'stroke',
                          pointerEvents: 'none',
                        }}
                      >
                        {ent.name.length > 16 ? ent.name.slice(0, 15) + '…' : ent.name}
                      </text>
                    )}
                  </g>
                );
              })}
            </g>
          </svg>

          {/* CONSOLIDATED VIEW CONTROLS (top-left, Lam 2026-06-24) — all the graph
              controls in ONE place by the Layout toggle: the Layout + Show-all chips
              stacked, then a row of the Filters ▾ + Lens ▾ dropdowns (opening
              DOWNWARD via dir='down'). The old bottom-left control bar is RETIRED;
              only the zoom cluster sits at the bottom now. renderLayoutChip /
              renderSpineChip self-gate (null when no orbital hubs / no floaters); the
              wrapper is pointer-events-none so the gaps still pan the canvas, while
              the chips + popovers are pointer-events-auto. A collapsed trigger still
              advertises its active children (count / lens name / dot). */}
          <div className="pointer-events-none absolute left-3.5 top-3 z-20 hidden flex-col items-start gap-1.5 sm:flex">
            {renderLayoutChip()}
            <div className="flex items-start gap-1.5">
              {renderSpineChip()}
              {renderMoreChip()}
            </div>
            <div className="flex items-start gap-1.5">
              <div className="relative">
                {renderMenuTrigger(
                  'filter',
                  activeFilterCount > 0 ? `Filters · ${activeFilterCount}` : 'Filters',
                  activeFilterCount > 0,
                )}
                {renderPopover('filter', filterPanel, 'down')}
              </div>
              {lensAvailable && (
                <div className="relative">
                  {renderMenuTrigger(
                    'lens',
                    lensOverlayOn ? 'Lens •' : 'Lens',
                    lensOverlayOn,
                  )}
                  {renderPopover('lens', lensPanel, 'down')}
                </div>
              )}
            </div>
          </div>

          {/* zoom controls (bottom-right) — lifted clear of the fixed MobileNav
              on <820px so the nav never eats their taps (pre-existing bug). */}
          <div
            className="absolute right-3 flex items-center gap-1.5"
            style={{ bottom: overlayBottom }}
          >
            <GraphButton label="Zoom out" onClick={() => zoomBy(1 / 1.25)}>
              −
            </GraphButton>
            <GraphButton label="Zoom in" onClick={() => zoomBy(1.25)}>
              +
            </GraphButton>
            <GraphButton label="Fit to view" onClick={fitToBounds}>
              <span className="font-mono text-[9px] uppercase tracking-[0.08em]">Fit</span>
            </GraphButton>
          </div>

          {/* hint (top-right) — yields to the path microcopy when path mode is on. */}
          {!pathMode && (
            <div className="pointer-events-none absolute right-3.5 top-3">
              <Mono size={10} color="var(--slate)">
                {isMobile ? 'Drag to pan · pinch to zoom · tap a node' : 'Drag · scroll to zoom · click a node'}
              </Mono>
            </div>
          )}

          {/* PATH FINDER microcopy (top-center) — guides the pick, then reports the
              resolved hop count (or the honest no-connection message). A small ×
              exits path mode. */}
          {pathMode && pathMicrocopy && (
            <div className="absolute left-1/2 top-3 flex max-w-[92%] -translate-x-1/2 items-center gap-2 rounded border-[0.5px] border-scout-gold/40 bg-surface/95 px-3 py-1.5 backdrop-blur-sm">
              <span
                aria-hidden
                className={`h-1.5 w-1.5 flex-shrink-0 rounded-full ${
                  pathResult && !pathResult.found ? 'bg-red-text' : 'bg-scout-gold'
                }`}
                style={pathResult && !pathResult.found ? { backgroundColor: 'var(--red-text)' } : { backgroundColor: 'var(--amber)' }}
              />
              <Mono size={10} color={pathResult && !pathResult.found ? 'var(--red-text)' : 'var(--parchment-dim)'}>
                {pathMicrocopy}
              </Mono>
              <button
                type="button"
                onClick={togglePathMode}
                aria-label="Exit path finder"
                className="-mr-1 cursor-pointer px-1 text-sm leading-none text-slate transition-colors hover:text-parchment"
              >
                ×
              </button>
            </div>
          )}
        </div>

        {/* ARROW LEGEND - the direction key. Without it an arrowhead is just a
            decoration; with it the reader can decode every spine edge on the map
            (review blocker 1). Sits under the canvas in the graph column so it
            reads with the picture, not with the controls. */}
        <p className="mt-2 font-mono text-[10px] leading-relaxed text-slate">
          Arrows point from the subject to what it founded / developed / is a subsidiary of /
          is built on. Dashed grey lines are inferred, not verified.
        </p>
        </div>

        {/* ── mobile control row ───────────────────────────────────────────
            The overlay bar is hidden under `sm`, so on phones the SAME grouping
            lives here directly below the canvas: Layout (if available) + Filter ▾
            + Lens ▾. The popovers reuse the one upward (bottom-full) primitive —
            opening UPWARD keeps them fully visible and clear of the fixed
            MobileNav (same clearance reasoning as overlayBottom; downward would
            collide with the nav). The row is items-end so the upward panels anchor
            cleanly above their triggers. */}
        <div className="flex flex-wrap items-end gap-1.5 sm:hidden">
          {renderLayoutChip()}
          {renderSpineChip()}
          {renderMoreChip()}
          <div className="relative">
            {renderMenuTrigger(
              'filter',
              activeFilterCount > 0 ? `Filters · ${activeFilterCount}` : 'Filters',
              activeFilterCount > 0,
            )}
            {renderPopover('filter', filterPanel)}
          </div>
          {lensAvailable && (
            <div className="relative">
              {renderMenuTrigger(
                'lens',
                lensOverlayOn ? 'Lens •' : 'Lens',
                lensOverlayOn,
              )}
              {renderPopover('lens', lensPanel)}
            </div>
          )}
        </div>

        {/* ── unified inspector ──────────────────────────────────────────────
            ≥768px: a non-obscuring sibling rail (desktop: right column; tablet:
            stacks below when selected). <768px: a fixed bottom sheet (below). */}
        {!isPhone && (
          <div className={pathInspect || edgeMode || (selected && (selectedEntity || selectedAsset)) ? '' : 'hidden lg:block'}>
            {inspector}
          </div>
        )}
      </div>

      {/* ── phone bottom sheet (portaled to <body>; dismissable; page scrolls free) ── */}
      {phoneSheet}
    </section>
  );
}

/* ── header ──────────────────────────────────────────────────────────────── */

function GraphHeader({
  chainLabel,
  entityCount,
  unconnectedCount,
  typedCount,
  typedTotal,
  inferredCount,
  inferredShown,
}: {
  chainLabel: string;
  /** ENTITY nodes on the canvas (asset satellites excluded — they aren't directory members). */
  entityCount: number;
  /** Members drawn on the outer rim because no typed edge reaches them YET. */
  unconnectedCount: number;
  /** CONFIRMED typed relationships DRAWN — the defensible claims on screen. */
  typedCount: number;
  /** CONFIRMED typed relationships that EXIST for this chain. */
  typedTotal: number;
  /** Untyped `related` co-occurrences across the FULL member set. */
  inferredCount: number;
  /** Whether those inferred links are currently on the canvas. */
  inferredShown: boolean;
}) {
  // EVIDENCE STRIP (2026-09-04). It used to read "59 mapped · 101 connections ·
  // 100% of directory": the 101 counted 64 untyped co-occurrences as connections,
  // and "100% of directory" was tautological (the scope defaulted to everything).
  // Now the two kinds of line are named separately and the hidden ones are
  // declared — a reader can see what the map is claiming and what it isn't.
  return (
    <header className="mb-4 flex flex-wrap items-end justify-between gap-x-4 gap-y-1">
      <div className="min-w-0">
        <span className="font-mono text-mono-label uppercase text-scout-gold">Relationship map</span>
        <h2 className="mt-1 font-display text-xl font-bold tracking-tight text-parchment sm:text-2xl">
          How {chainLabel} entities connect
        </h2>
      </div>
      {entityCount > 0 && (
        <span className="font-mono text-[11px] text-parchment-dim sm:flex-shrink-0 sm:text-right">
          {entityCount} mapped ·{' '}
          <span className="text-scout-gold">
            {typedCount < typedTotal
              ? `${typedCount} of ${typedTotal} typed relationships shown`
              : `${typedTotal} typed relationship${typedTotal === 1 ? '' : 's'}`}
          </span>
          {inferredCount > 0 && (
            <span className="text-slate">
              {' '}
              · {inferredCount} inferred link{inferredCount === 1 ? '' : 's'}
              {inferredShown ? '' : ' (hidden)'}
            </span>
          )}
          {unconnectedCount > 0 && (
            <span className="text-slate">
              {' '}
              · {unconnectedCount}{' '}
              {unconnectedCount === 1 ? 'entity' : 'entities'} not yet connected
            </span>
          )}
        </span>
      )}
    </header>
  );
}

/* ── small round canvas control ──────────────────────────────────────────── */

function GraphButton({
  children,
  label,
  onClick,
}: {
  children: React.ReactNode;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      className="flex h-7 min-w-[28px] items-center justify-center rounded border-[0.5px] border-divider bg-surface px-1.5 font-mono text-[15px] leading-none text-parchment-dim transition-colors hover:border-scout-gold/40 hover:text-parchment"
    >
      {children}
    </button>
  );
}

/* ── info rail (the non-obscuring affordance) ────────────────────────────── */

function GraphRail({
  entity,
  asset = null,
  edges = null,
  path = null,
  onClosePath,
  relationships,
  portfolio = [],
  onSelect,
  onInspectEdge,
  onCloseEdge,
  inChain,
  isMobile,
  desktopHeight,
  sheet = false,
  onClose,
  dragHandleProps,
  reducedMotion = false,
}: {
  entity: ChainEntity | null;
  /** Selected ASSET satellite — renders the compact asset card variant (wins over entity). */
  asset?: ChainAssetNode | null;
  /** EDGE MODE (edge-as-first-class): the typed edge(s) on the selected pair. When
      non-null/non-empty, the rail renders EDGE mode and WINS over entity/asset. */
  edges?: RailEdge[] | null;
  /** PATH READ (path finder): the resolved connection chain as hop rows. When
      present (and no node/edge selected), the rail renders the PATH card. */
  path?: { hops: RailPathHop[]; from: string; to: string } | null;
  /** PATH card × → exit path finder. */
  onClosePath?: () => void;
  /** The entity's TYPED edges, already perspectivized (label + other endpoint + narrative). */
  relationships: RailRelationship[];
  /** The entity's member-asset portfolio (identity tokens pre-excluded) — the
      ASSETS block rows; junction-rule leaves live HERE instead of on the canvas. */
  portfolio?: ChainAssetNode[];
  onSelect: (slug: string) => void;
  /** Promote a relationship counterpart to EDGE mode (the row's ◆ affordance). */
  onInspectEdge?: (otherSlug: string) => void;
  /** EDGE mode × → back to the node inspector. */
  onCloseEdge?: () => void;
  inChain: Map<string, ChainEntity>;
  isMobile: boolean;
  /** Desktop rail height = the canvas height, so the section never changes height
      between entities (the connections list scrolls internally instead). */
  desktopHeight: number;
  /** Rendered inside the phone bottom sheet (rounded top, vh cap, close affordance). */
  sheet?: boolean;
  onClose?: () => void;
  /** Sheet only: swipe-down-to-dismiss pointer handlers, spread on the grab
      handle + header ONLY (the scrollable body must keep native panning). */
  dragHandleProps?: SheetDragHandlers;
  /** R7: instant scroll + no entrance animation under prefers-reduced-motion. */
  reducedMotion?: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);

  // Tablet (768–1024): the rail appears BELOW the graph — bring it into view on
  // select. (Phones use the fixed sheet — nothing to scroll to.)
  useEffect(() => {
    if (isMobile && !sheet && (entity || asset) && ref.current) {
      ref.current.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'nearest' });
    }
  }, [entity, asset, isMobile, sheet, reducedMotion]);

  // Desktop: pin the rail to the canvas height (mobile: natural, capped). This is what
  // kills the layout shift — the panel is the SAME size for every entity; only its
  // internal connections list scrolls.
  const railHeight = isMobile || sheet ? undefined : desktopHeight;

  // Sheet: capped at ~70dvh (dynamic viewport unit — tracks the collapsing
  // mobile URL bar; the 70vh class is the no-dvh-support fallback, the inline
  // style below wins where dvh exists) so the page peeks behind; ONLY the
  // body scrolls.
  const shellCls = `flex flex-col overflow-hidden border-[0.5px] border-divider border-t-2 border-t-scout-gold/70 bg-surface ${
    sheet ? 'max-h-[70vh] rounded-t-lg' : 'max-h-[75vh] rounded lg:max-h-none'
  }`;
  const shellStyle: React.CSSProperties = {
    height: railHeight,
    maxHeight: sheet ? '70dvh' : undefined,
    // Inspector-open motion (R7): a 240ms fade-up, replayed per selection (the
    // shell divs are KEYED by the selected entity/asset, so switching nodes
    // remounts them). Default fill mode — the transform clears when done, so
    // it never lingers as a containing block. CSS-gated by eg-motion-scope;
    // JS-gated here too so the hook and the stylesheet agree.
    animation: reducedMotion ? undefined : 'eg-rail-in 240ms ease-out',
  };
  // Drag-to-dismiss region (grab handle + header) — touch-none so the browser
  // hands the gesture to our pointer handlers instead of scrolling anything.
  const dragRegionProps = sheet && dragHandleProps ? dragHandleProps : {};
  const dragRegionCls = sheet && dragHandleProps ? ' touch-none select-none cursor-grab active:cursor-grabbing' : '';
  const grabHandle = sheet ? (
    <div {...dragRegionProps} className={`flex-shrink-0 px-4 pb-1 pt-2.5${dragRegionCls}`}>
      <div className="mx-auto h-1 w-10 rounded-full bg-divider" aria-hidden />
    </div>
  ) : null;

  /* ── PATH READ (path finder "the connection"): the resolved chain as hop rows —
       A —label→ B —label→ C, each typed hop carrying its narrative. A shareable
       narrative artifact. Wins when present (only set when no node/edge selected). */
  if (path && path.hops.length > 0) {
    return (
      <div key={`path:${path.from}:${path.to}`} ref={ref} className={shellCls} style={shellStyle}>
        {grabHandle}
        <div
          {...dragRegionProps}
          className={`flex flex-shrink-0 items-center justify-between gap-2 border-b border-divider p-4${sheet ? ' pt-1.5' : ''}${dragRegionCls}`}
        >
          <span className="min-w-0">
            <Eyebrow color="var(--amber)">The connection</Eyebrow>
            <p className="mt-1 truncate font-display text-[13px] font-semibold text-parchment">
              {path.from} → {path.to}
              <span className="ml-1.5 font-mono text-[10px] font-normal text-parchment-dim">
                · {path.hops.length} {path.hops.length === 1 ? 'hop' : 'hops'}
              </span>
            </p>
          </span>
          {(onClosePath || (sheet && onClose)) && (
            <RailCloseButton onClose={sheet && onClose ? onClose : onClosePath!} />
          )}
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-4 scrollbar-hide">
          <ol className="flex flex-col">
            {path.hops.map((h, i) => (
              <li key={`${h.from.slug}-${h.to.slug}-${i}`}>
                {/* the FROM node — the first node is shown; each next hop's FROM is
                    the previous TO, so we only render the destination on later hops. */}
                {i === 0 && <PathNodeRow node={h.from} onSelect={onSelect} />}
                {/* the relationship label connector */}
                <div className="flex items-center gap-2 py-1.5 pl-4">
                  <span aria-hidden className="text-[13px] leading-none text-slate">
                    ↓
                  </span>
                  <Mono size={9} color={h.typed ? 'var(--amber)' : 'var(--slate)'} style={{ letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                    {h.label ?? 'linked'}
                  </Mono>
                </div>
                {h.description && (
                  <p className="mb-1 ml-6 border-l border-divider pl-3 font-display text-[11.5px] leading-relaxed text-slate-light">
                    {h.description}
                  </p>
                )}
                <PathNodeRow node={h.to} onSelect={onSelect} />
              </li>
            ))}
          </ol>
        </div>
        <div
          className="flex-shrink-0 border-t border-divider p-3.5"
          style={sheet ? { paddingBottom: 'max(14px, env(safe-area-inset-bottom))' } : undefined}
        >
          <Mono size={9} color="var(--slate)" style={{ letterSpacing: '0.06em' }}>
            Shareable: append{' '}
            <span className="text-parchment-dim">
              ?path={path.hops[0].from.slug},{path.hops[path.hops.length - 1].to.slug}
            </span>
          </Mono>
        </div>
      </div>
    );
  }

  /* ── EDGE mode (edge-as-first-class): the relationship itself is the subject ──
       A —TYPE→ B header (both clickable), narrative verbatim, confidence, sources
       count, added date, "view both in dossier". Wins over entity/asset. */
  if (edges && edges.length > 0) {
    return (
      <div key={`edge:${edges[0].from.slug}:${edges[0].to.slug}`} ref={ref} className={shellCls} style={shellStyle}>
        {grabHandle}
        <div
          {...dragRegionProps}
          className={`flex flex-shrink-0 items-center justify-between gap-2 border-b border-divider p-4${sheet ? ' pt-1.5' : ''}${dragRegionCls}`}
        >
          <Eyebrow color="var(--amber)">Relationship</Eyebrow>
          {(onCloseEdge || (sheet && onClose)) && (
            <RailCloseButton onClose={sheet && onClose ? onClose : onCloseEdge!} />
          )}
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-4 scrollbar-hide">
          {edges.map((e, i) => (
            <div key={`${e.type}-${i}`} className={i > 0 ? 'mt-4 border-t border-divider pt-4' : ''}>
              {/* A —TYPE→ B header — both endpoints clickable to focus the node. */}
              <div className="flex items-center gap-2">
                <EdgeEndpoint entity={e.from} onSelect={onSelect} />
                <span className="flex flex-shrink-0 flex-col items-center">
                  <Mono size={9} color="var(--amber)" style={{ letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                    {e.label}
                  </Mono>
                  <span className="text-[11px] leading-none text-slate" aria-hidden>
                    →
                  </span>
                </span>
                <EdgeEndpoint entity={e.to} onSelect={onSelect} />
              </div>

              {/* the narrative — verbatim (the product). */}
              {e.description && (
                <p className="mt-3 font-display text-[12.5px] leading-relaxed text-slate-light">
                  {e.description}
                </p>
              )}

              {/* EVIDENCE ROW (2026-09-04) — type · role · since · confidence ·
                  added. The inspector was already the strongest part of the map;
                  what it lacked was the ROLE, the start date and WHO SAYS SO. */}
              <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1.5">
                <span className="inline-flex items-center gap-1 rounded border-[0.5px] border-scout-gold/30 px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-[0.06em] text-scout-gold">
                  {(e.type ?? 'ASSOCIATED_WITH').replace(/_/g, ' ')}
                </span>
                {e.role && (
                  <Mono size={9} color="var(--parchment-dim)" style={{ letterSpacing: '0.04em' }}>
                    role: {e.role}
                  </Mono>
                )}
                {e.startDate && (
                  <Mono size={9} color="var(--parchment-dim)" style={{ letterSpacing: '0.04em' }}>
                    since {edgeAddedLabel(e.startDate) || e.startDate}
                  </Mono>
                )}
                <span className="inline-flex items-center gap-1 rounded border-[0.5px] border-divider px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-[0.06em] text-parchment-dim">
                  {e.confidence} confidence
                </span>
                {e.createdAt && (
                  <Mono size={9} color="var(--slate)" style={{ letterSpacing: '0.04em' }}>
                    added {edgeAddedLabel(e.createdAt)}
                  </Mono>
                )}
              </div>

              {/* SOURCES — hostnames only. The full url, the quoted snippet and
                  the asserter (model ids, scrape internals) stay server-side; the
                  reader gets the one thing that matters, which outlet said it.
                  A source with no parseable url still counts, hence the remainder. */}
              {e.sourceCount > 0 && (
                <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1">
                  <Mono size={9} color="var(--slate)" style={{ letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                    {e.sourceCount} {e.sourceCount === 1 ? 'source' : 'sources'}
                  </Mono>
                  {e.sourceHosts.map((h) => (
                    <span
                      key={h}
                      className="inline-flex items-center rounded border-[0.5px] border-divider bg-ink px-1.5 py-0.5 font-mono text-[9px] text-parchment-dim"
                    >
                      {h}
                    </span>
                  ))}
                  {e.sourceCount > e.sourceHosts.length && e.sourceHosts.length > 0 && (
                    <Mono size={9} color="var(--slate)">
                      +{e.sourceCount - e.sourceHosts.length}
                    </Mono>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
        {/* view both in dossier — links to each endpoint's canonical page. */}
        <div
          className="flex flex-shrink-0 items-center gap-4 border-t border-divider p-3.5"
          style={sheet ? { paddingBottom: 'max(14px, env(safe-area-inset-bottom))' } : undefined}
        >
          <Mono size={9} color="var(--slate)" style={{ letterSpacing: '0.08em', textTransform: 'uppercase' }}>
            View both:
          </Mono>
          <Link
            href={`/tags/${edges[0].from.slug}`}
            className="truncate font-mono text-[11px] uppercase tracking-[0.06em] text-scout-gold transition-opacity hover:opacity-80"
          >
            {edges[0].from.name} →
          </Link>
          <Link
            href={`/tags/${edges[0].to.slug}`}
            className="truncate font-mono text-[11px] uppercase tracking-[0.06em] text-scout-gold transition-opacity hover:opacity-80"
          >
            {edges[0].to.name} →
          </Link>
        </div>
      </div>
    );
  }

  /* ── ASSET card variant (compact): name · type · parents · market deep-link ── */
  if (asset) {
    const isToken = asset.assetType === 'token';
    const marketHref = isToken
      ? `/market?tab=tokens&asset=${asset.assetId.toLowerCase()}`
      : `/market?tab=nfts&asset=${asset.assetId.toLowerCase()}`;
    const parents = asset.parentSlugs
      .map((s) => inChain.get(s))
      .filter((e): e is ChainEntity => !!e);
    return (
      <div key={asset.id} ref={ref} className={shellCls} style={shellStyle}>
        {grabHandle}
        <div
          {...dragRegionProps}
          className={`flex flex-shrink-0 gap-3 border-b border-divider p-4${sheet ? ' pt-1.5' : ''}${dragRegionCls}`}
        >
          {asset.image ? (
            // eslint-disable-next-line @next/next/no-img-element
            <img src={asset.image} alt="" className="h-11 w-11 flex-shrink-0 rounded object-cover" />
          ) : (
            <Avatar name={asset.name} type="token" size={44} />
          )}
          <div className="min-w-0 flex-1">
            <h3 className="truncate font-display text-base font-bold tracking-tight text-parchment">
              {asset.name}
            </h3>
            <div className="mt-0.5 flex items-center gap-2">
              <Mono size={10} color="var(--slate)" style={{ letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                {isToken ? 'Token' : 'NFT collection'}
              </Mono>
              <Mono size={10} color="var(--slate)">
                · linked asset
              </Mono>
            </div>
          </div>
          {sheet && onClose && <RailCloseButton onClose={onClose} />}
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-4 scrollbar-hide">
          <p className="font-display text-[12.5px] leading-relaxed text-slate-light">
            {isToken ? 'A token' : 'An NFT collection'} linked to{' '}
            {parents.length === 1 ? 'an entity' : `${parents.length} entities`} in this
            ecosystem&apos;s graph.
          </p>
          {parents.length > 0 && (
            <div className="mt-4">
              <div className="mb-2 flex items-center gap-2">
                <Eyebrow color="var(--amber)">Asset of</Eyebrow>
                <Mono size={10} color="var(--slate)">
                  {parents.length}
                </Mono>
              </div>
              <div className="flex flex-col gap-1.5">
                {parents.map((p) => (
                  <button
                    key={p.slug}
                    type="button"
                    onClick={() => onSelect(p.slug)}
                    className="group flex w-full items-center gap-2 rounded border-[0.5px] border-divider bg-ink p-1.5 text-left transition-colors hover:border-scout-gold/40"
                  >
                    {p.image ? (
                      // eslint-disable-next-line @next/next/no-img-element
                      <img src={p.image} alt="" loading="lazy" className="h-6 w-6 flex-shrink-0 rounded object-cover" />
                    ) : (
                      <Avatar name={p.name} type={p.type} size={24} />
                    )}
                    <span className="min-w-0 flex-1 truncate font-display text-[12px] font-semibold text-parchment">
                      {p.name}
                    </span>
                    <Mono size={9} color="var(--slate)" style={{ letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                      {ENTITY_TYPES[p.type as EntityTypeKey]?.label ?? p.type}
                    </Mono>
                    {/* visible "this row inspects the entity" affordance */}
                    <span
                      aria-hidden
                      className="ml-0.5 flex-shrink-0 font-mono text-[10px] text-slate-light transition-colors group-hover:text-scout-gold"
                    >
                      →
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
        <div
          className="flex-shrink-0 border-t border-divider p-3.5"
          style={sheet ? { paddingBottom: 'max(14px, env(safe-area-inset-bottom))' } : undefined}
        >
          <Link
            href={marketHref}
            className="inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.1em] text-scout-gold transition-opacity hover:opacity-80"
          >
            View market →
          </Link>
        </div>
      </div>
    );
  }

  if (!entity) {
    // Desktop idle state (hidden on mobile until a node is tapped).
    return (
      <div
        className="flex flex-col justify-center rounded border-[0.5px] border-divider bg-surface p-5"
        style={{ height: railHeight }}
      >
        <Eyebrow color="var(--slate)">Inspect</Eyebrow>
        <p className="mt-2 font-display text-[13px] leading-relaxed text-parchment-dim">
          Click any node to inspect that entity and the connections it shares with the rest of
          the ecosystem — without leaving the map.
        </p>
      </div>
    );
  }

  const tok = entity.token;
  // Generic connections de-duped against typed relationships (typed wins — the
  // same counterpart never appears in both lists).
  const typedSlugs = new Set(relationships.map((r) => r.other.slug));
  const genericRelated = entity.related.filter((r) => !typedSlugs.has(r.slug));

  // The SHARED content core (EntityProfileContent — one content component, two
  // containers: this rail/sheet + the shell-level EntityPreviewDrawer). This
  // shell keeps the graph-specific mechanics: height pinning, the grab handle,
  // drag-to-dismiss wiring, the keyed fade-up entrance (R7).
  // GUEST header line ("Off-chain · Ethereum"): the inspector is naturally thin
  // for guests — no token chip / portfolio / generic connections (all member-only
  // joins the server never makes for guests) — so the banner is the one explicit
  // marker; everything below degrades gracefully via the shared content core.
  const homeLabel = entity.offChain ? guestHomeLabel(entity.offChain) : null;
  // Affirmative home identity ("Lives on Ethereum") rather than a caveat: an
  // off-chain entity is a real place, not a second-class visitor (de-ghost,
  // 2026-06-24). The dot wears the home chain's identity color; the chain-
  // agnostic 'other' sentinel (no named home) keeps a neutral slate dot + "Off-chain".
  const homeColor: string = entity.offChain
    ? getChain(entity.offChain)?.color ?? 'var(--slate-light)'
    : 'var(--slate-light)'; // never rendered (banner is offChain-gated) — keeps the type a clean string
  const guestBanner = entity.offChain ? (
    <div className="flex flex-shrink-0 items-center gap-2 border-b border-divider bg-ink/40 px-4 py-2">
      <span
        aria-hidden
        className="h-2.5 w-2.5 flex-shrink-0 rounded-full"
        style={{ background: homeColor }}
      />
      <Mono
        size={10}
        color="var(--parchment-dim)"
        style={{ letterSpacing: '0.08em', textTransform: 'uppercase' }}
      >
        {homeLabel ? `Lives on ${homeLabel}` : 'Off-chain'}
      </Mono>
    </div>
  ) : null;

  return (
    <div key={entity.slug} ref={ref} className={shellCls} style={shellStyle}>
      {grabHandle}
      {guestBanner}
      <EntityProfileContent
        entity={entity}
        // ONE concise identity line (seoExcerpt ?? description, server-truncated) —
        // NOT the full About; fresh intel below is the product (Lam, 2026-06-09).
        brief={entity.brief || entity.desc}
        recon={entity.recon}
        token={tok ? { symbol: tok.symbol, change: tok.change } : null}
        relationships={relationships}
        // Each relationship row gets a ◆ that promotes the edge to EDGE mode.
        onInspectRelationship={onInspectEdge}
        portfolio={portfolio}
        connections={genericRelated}
        // In-chain → re-focus the node in place; off-chain → its canonical page.
        isConnectionSelectable={(slug) => inChain.has(slug)}
        onSelect={onSelect}
        sheet={sheet}
        dragRegionProps={dragRegionProps}
        dragRegionCls={dragRegionCls}
        headerRight={sheet && onClose ? <RailCloseButton onClose={onClose} /> : undefined}
      />
    </div>
  );
}

/** A path-chain node row (clickable → focuses that node, exiting path mode). */
function PathNodeRow({
  node,
  onSelect,
}: {
  node: RailPathHop['from'];
  onSelect: (slug: string) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onSelect(node.slug)}
      className="flex w-full items-center gap-2 rounded border-[0.5px] border-divider bg-ink p-1.5 text-left transition-colors hover:border-scout-gold/40"
    >
      {node.image ? (
        // eslint-disable-next-line @next/next/no-img-element
        <img src={node.image} alt="" loading="lazy" className="h-6 w-6 flex-shrink-0 rounded object-cover" />
      ) : (
        <Avatar name={node.name} type={node.isAsset ? 'token' : 'project'} size={24} />
      )}
      <span className="min-w-0 flex-1 truncate font-display text-[12.5px] font-semibold text-parchment">
        {node.name}
      </span>
      {node.isAsset && (
        <Mono size={9} color="var(--slate)" style={{ letterSpacing: '0.06em', textTransform: 'uppercase' }}>
          asset
        </Mono>
      )}
    </button>
  );
}

/** A clickable endpoint chip in the EDGE-mode header (focuses that node). */
function EdgeEndpoint({
  entity,
  onSelect,
}: {
  entity: ChainEntity;
  onSelect: (slug: string) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onSelect(entity.slug)}
      className="flex min-w-0 flex-1 items-center gap-1.5 rounded border-[0.5px] border-divider bg-ink p-1.5 text-left transition-colors hover:border-scout-gold/40"
    >
      {entity.image ? (
        // eslint-disable-next-line @next/next/no-img-element
        <img src={entity.image} alt="" loading="lazy" className="h-6 w-6 flex-shrink-0 rounded object-cover" />
      ) : (
        <Avatar name={entity.name} type={entity.type} size={24} />
      )}
      <span className="min-w-0 flex-1 truncate font-display text-[12px] font-semibold text-parchment">
        {entity.name}
      </span>
    </button>
  );
}

/** "added Jun 10" / "added 2025" label for an edge createdAt ISO. */
function edgeAddedLabel(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const sameYear = d.getFullYear() === new Date().getFullYear();
  return sameYear
    ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    : d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
}

/** Close affordance for the phone bottom-sheet inspector. */
function RailCloseButton({ onClose }: { onClose: () => void }) {
  return (
    <button
      type="button"
      onClick={onClose}
      aria-label="Close inspector"
      className="-mr-1 -mt-1 cursor-pointer self-start p-1 text-xl leading-none text-parchment-dim transition-colors hover:text-parchment"
    >
      ×
    </button>
  );
}

/* The RECON block + Recent-coverage list moved to ./EntityProfileContent.tsx —
   the ONE shared content core this rail and EntityPreviewDrawer both wrap. */
